// Single source of pt-PT strings for the dashboard. Add keys here, import everywhere.
// User-facing text MUST live in this file — English strings in components are a bug.

export const pt = {
  app: {
    title: 'Centro de Operações',
    bootstrapStub: 'Plataforma em preparação — Fase 1 em curso.',
  },
  auth: {
    signIn: 'Iniciar sessão',
    signInSubtitle: 'JA Consultores · Centro de Operações',
    signOut: 'Terminar sessão',
    choosePrincipal: 'Escolha o acesso',
    choosePrincipalHint: 'A sua conta tem acesso a mais do que um escritório.',
    principalClient: 'Portal de cliente',
    principalActive: 'Ativo',
    switchPrincipal: 'Mudar de acesso',
    email: 'Email',
    // Mostrado quando o endereço não corresponde a nenhuma conta. Contas são criadas por convite
    // (staff) ou pelo provisionamento do portal — nunca por quem escreve um email nesta caixa.
    unknownEmail:
      'Não existe nenhuma conta com este email. As contas são criadas por convite — fale com o administrador do escritório.',
    password: 'Palavra-passe',
    mfaPrompt: 'Código de autenticação',
    mfaVerify: 'Verificar',
    mfaEnrollTitle: 'Ativar autenticação em dois passos',
    mfaEnrollIntro:
      'Digitalize o código QR com a sua app de autenticação (Google Authenticator, 1Password, Authy). Depois introduza o código de 6 dígitos para confirmar.',
    mfaEnrollSecret: 'Não consegue digitalizar? Introduza este código manualmente:',
    mfaChallengeTitle: 'Código de autenticação',
    mfaChallengeIntro: 'Abra a sua app de autenticação e introduza o código atual de 6 dígitos.',
    mfaCodeLabel: 'Código (6 dígitos)',
    mfaInvalidCode: 'Código inválido ou expirado. Tente novamente.',
    unauthenticated: 'Sessão não iniciada',
    unprovisioned: 'A sua conta ainda não foi provisionada. Contacte um administrador.',
    forbidden: 'Não tem permissão para aceder a esta área.',
    loading: 'A carregar…',
    backToOps: 'Voltar ao Centro de Operações',
    forgotPassword: 'Esqueci a palavra-passe',
    forgotPasswordIntro:
      'Introduza o seu email e enviaremos um link mágico para iniciar sessão sem palavra-passe.',
    sendMagicLink: 'Enviar link mágico',
    backToSignIn: '← Voltar para iniciar sessão',
    magicLinkSent: 'Verifique a sua caixa de entrada — enviámos um link de acesso para {email}.',
  },
  common: {
    loading: 'A carregar…',
    save: 'Guardar',
    saving: 'A guardar…',
    cancel: 'Cancelar',
    create: 'Criar',
    edit: 'Editar',
    delete: 'Eliminar',
    confirm: 'Confirmar',
    close: 'Fechar',
    search: 'Pesquisar…',
    none: 'Nenhum',
    yes: 'Sim',
    no: 'Não',
    required: 'Obrigatório',
    optional: 'Opcional',
    active: 'Ativo',
    inactive: 'Inativo',
    all: 'Todos',
  },
  portal: {
    backToTop: 'Voltar ao topo',
    // The way OUT of the portal, at the top of the sidebar — phrased as a return
    // because that is where these users came from (SSO). Not the staff sidebar's
    // 'SAFT AI', which labels a destination in a list of destinations.
    backToSaftAi: 'Voltar a SAFT.AI',
    // Novidades — the admin-curated announcements. Client register throughout: Nuno's objection
    // on the 2026-09-28 call was specifically that workshop-grade technical language must not
    // reach clients, and that applies to the chrome around the entries too.
    novidades: {
      nav: 'Novidades',
      title: 'Novidades',
      subtitle: 'O que mudou na plataforma, explicado sem termos técnicos.',
      empty: 'Ainda não há novidades para mostrar.',
      newPill: 'Nova',
      openMedia: 'Ver demonstração',
      loadFailed: 'Não foi possível carregar as novidades.',
    },
    // Roberto — the document assistant. `name` is the ONE place the assistant is
    // named: Jorge floated "Intelligence" on the 2026-09-23 call and the decision
    // is still open, so nothing else in the codebase hardcodes it.
    roberto: {
      name: 'Roberto',
      launch: 'Perguntar ao Roberto',
      close: 'Fechar',
      newThread: 'Nova conversa',
      subtitle: 'Sobre os documentos de {name}',
      emptyTitle: 'Pergunta-me sobre os teus documentos.',
      emptyText:
        'Procuro em tudo o que está escrito nos documentos desta empresa — fornecedor, linhas da fatura, texto da página — e respondo com os documentos que usei, para os poderes abrir ou descarregar.',
      suggestions: [
        'Onde está a fatura do computador que comprei este ano?',
        'Quanto gastei em restaurantes este mês?',
        'Que faturas tenho da Galp neste trimestre?',
      ],
      placeholder: 'Escreve a tua pergunta…',
      send: 'Enviar',
      thinking: 'A ler os teus documentos…',
      open: 'Abrir',
      download: 'Descarregar',
      downloadFailed: 'Não foi possível descarregar o documento.',
      noTotal: 'sem total',
      // The catch-all. Everything that goes wrong and is NOT something the user
      // can act on says exactly this, in Roberto's own voice — a client should
      // never meet a technical failure, and "tenta outra vez dentro de um
      // momento" is a lie when the cause is not transient. The three messages
      // below it are the only exceptions, because each names an action.
      cantAnswer:
        'Não consegui responder a essa. Tenta pôr a pergunta de outra maneira — por exemplo com o nome do fornecedor, o tipo de despesa ou o mês.',
      // The honesty line. It stays visible under the composer rather than in a
      // dismissible notice: the assistant can be wrong, and the documents it
      // cites are the thing to trust.
      disclaimer:
        'O Roberto pode enganar-se. Confirma sempre no documento — é por isso que ele indica quais usou.',
    },
    efatura: {
      nav: 'e-Fatura',
      title: 'e-Fatura',
      subtitle:
        'Faturas de compra comunicadas à AT em nome da sua empresa, cruzadas com os documentos já entregues.',
      // The portal never pulls from AT itself — data appears after the accounting team syncs it.
      neverPulled:
        'Ainda sem dados do e-Fatura para esta empresa. A equipa de contabilidade fará a primeira sincronização em breve.',
      missingTitle:
        'Esta fatura consta do e-Fatura mas ainda não recebemos o documento. Por favor envie-o.',
    },
    bank: {
      nav: 'Contas bancárias',
      title: 'Contas bancárias',
      subtitle:
        'Ligue as contas bancárias de {name} para importarmos os movimentos automaticamente.',
      pickBank: 'Banco',
      connect: 'Ligar banco',
      connecting: 'A redirecionar…',
      empty: 'Ainda não há contas ligadas. Escolha o banco e clique em «Ligar banco».',
      connected: 'Conta bancária ligada com sucesso. Os movimentos começarão a aparecer em breve.',
      connectError: 'Não foi possível concluir a ligação ao banco. Tente novamente.',
      startError: 'Não foi possível iniciar a ligação. Tente novamente.',
      expires: 'Consentimento válido até {date}.',
    },
    documentos: {
      supplierLabel: 'Fornecedor',
      supplierAll: 'Fornecedor: todos',
      supplierSearchPlaceholder: 'Procurar fornecedor…',
    },
    // "Pedir eliminação": the client never deletes — the request notifies the accounting team,
    // who decide. The copy has to make that explicit or a client will assume the file is gone.
    deletionRequest: {
      action: 'Pedir eliminação',
      pending: 'Eliminação pedida',
      pendingTitle: 'Já pediu a eliminação deste documento. A equipa de contabilidade vai avaliar.',
      confirmTitle: 'Pedir a eliminação deste documento?',
      confirmBody:
        'O documento NÃO é eliminado agora. A equipa de contabilidade recebe o seu pedido e decide.',
      confirmCta: 'Enviar pedido',
      reasonLabel: 'Motivo do pedido',
      reasonPlaceholder: 'Ex.: documento pessoal carregado por engano…',
      reasonRequired: 'Indique o motivo para a equipa poder avaliar o pedido.',
      sent: 'Pedido enviado à equipa de contabilidade.',
      failed: 'Não foi possível enviar o pedido. Tente novamente.',
    },
    // "Perguntar ao contabilista": the client saw the platform file a document wrongly and
    // wants to tell someone, without being able to change it themselves.
    question: {
      action: 'Perguntar ao contabilista',
      pending: 'Pergunta enviada',
      pendingTitle:
        'Já colocou uma questão sobre este documento. A equipa de contabilidade vai responder.',
      title: 'Perguntar ao contabilista',
      body: 'Diga-nos o que está errado neste documento. A equipa de contabilidade recebe a sua mensagem e trata do assunto.',
      messageLabel: 'A sua mensagem',
      messagePlaceholder: 'Ex.: isto é uma fatura-recibo, não é um documento de arquivo.',
      messageRequired: 'Escreva a sua questão para a equipa poder responder.',
      cta: 'Enviar',
      sent: 'Pergunta enviada à equipa de contabilidade.',
      failed: 'Não foi possível enviar a pergunta. Tente novamente.',
      submit: 'Enviar pedido',
      bodyWithFields:
        'Diga-nos o que está errado neste documento. Se souber, corrija também os campos abaixo — a equipa de contabilidade analisa e confirma.',
      fieldsTitle: 'Dados do documento',
      fieldsHint: 'Altere apenas o que estiver errado. O que não mexer fica como está.',
      noFieldsChanged: 'Nenhum campo alterado.',
      fieldsChanged: '{n} campo(s) a corrigir.',
    },
  },
  documents: {
    states: {
      uploading: 'A carregar',
      received: 'Recebido',
      identified: 'Identificado',
      routed: 'Encaminhado',
      validated: 'Validado',
      reconciled: 'Reconciliado',
      closed: 'Fechado',
      error: 'Erro',
    },
    // Canonical bucket vocabulary (2026-06 redesign). Cards on
    // /operacoes/documentos + /operacoes/cliente/:id: Todos (total) + the four
    // below. Source of truth: apps/web/src/lib/documentsView.ts.
    buckets: {
      em_curso: 'Por Encaminhar',
      pendente: 'Pendentes',
      erro: 'Erro',
      arquivo_fiscal: 'Arquivo Fiscal',
      arquivo_outros: 'Arquivo Outros',
      dispensado: 'Dispensados',
    },
    bucketDescriptions: {
      em_curso:
        'Recebidos ou já identificados, prontos a seguir mas ainda não encaminhados (p. ex. aguardam que o envio automático seja activado para o cliente). Filtre por este estado no menu Estado.',
      pendente:
        'A IA identificou o documento mas detectou algo que requer revisão humana antes de encaminhar — confiança baixa, NIF ambíguo, ou cliente sem correspondência directa.',
      erro: 'Dois tipos de erro (filtráveis no Estado como "Erro · Documento" / "Erro · Envio", e com a razão sob o estado): Documento — não foi possível processar o ficheiro (PDF corrupto, imagem ilegível, erro de API/IA); Envio — o documento seguiu para o ERP mas voltou com erro (rejeitado pelo TOConline/Contis, email devolvido). Abra e edite manualmente para o repor em circulação.',
      arquivo_fiscal:
        'Documentos contabilísticos (faturas, recibos, pagamentos ao Estado) já encaminhados / finalizados no sistema do cliente (TOConline / Contis).',
      arquivo_outros:
        'Registos relevantes para a empresa mas SEM validade contabilística — escrituras, extratos, contratos, certidões, mapas. Catalogados e guardados, nunca encaminhados.',
      dispensado:
        'Ficheiros sem qualquer valor — imagens de assinatura, avatares, ruído de email. Dispensados (ocultos); o ficheiro é mantido e a acção é reversível.',
    },
    bucketsAllLabel: 'Todos os Documentos',
    // Buckets an operator can assign from the edit dialog (StateBucket). 'outros'
    // hand-files to Arquivo Outros (sets the `outros` label). Dispensar is a
    // separate column-flag action, not a state bucket.
    stateBuckets: {
      em_curso: 'Por Encaminhar',
      pendente: 'Pendentes',
      outros: 'Arquivar em Outros',
      erro: 'Erro',
    },
    dismiss: 'Dispensar',
    restore: 'Restaurar',
  },
  admin: {
    suppliers: {
      title: 'Fornecedores',
      subtitle:
        'O registo central de fornecedores: identidade, origem dos dados e estado de validação.',
      globalNotice:
        'Os fornecedores são partilhados por todos os escritórios da plataforma. Uma alteração aqui é visível para todos.',
      filterValidation: 'Validação',
      filterProvenance: 'Origem',
      searchPlaceholder: 'Nome, NIF, alias ou variante…',
      colName: 'Fornecedor',
      // "Alias" = curated commercial umbrella (many legal companies, one spend group — Uber's
      // fleet partners). Distinct from "Variantes" (observed name spellings, supplier_aliases).
      colBrand: 'Alias',
      colIdentity: 'NIF / ID fiscal',
      colScope: 'Origem',
      colValidation: 'Validação',
      colProvenance: 'Fonte do nome',
      colDocs: 'Docs',
      colAliases: 'Variantes',
      noName: 'Sem nome atribuído',
      empty: 'Nenhum fornecedor corresponde aos filtros escolhidos.',
      countOne: 'fornecedor',
      countMany: 'fornecedores',
      duplicatesLink: 'Rever duplicados →',
    },
    // Platform-operator surface. A firm arrives from the SAFT-AI sync with only a name — where its
    // documents arrive and which ERP they leave through are ours to set, and it is not polled until
    // they are.
    firms: {
      title: 'Escritórios',
      subtitle: 'Configuração de cada escritório: entrada de documentos e ERP de destino.',
      unconfiguredTitle: 'Escritórios por configurar',
      unconfiguredHint: (n: number) =>
        n === 1
          ? '1 escritório ainda não recebe documentos — falta configuração.'
          : `${n} escritórios ainda não recebem documentos — falta configuração.`,
      ready: 'Configurado',
      missingCount: (n: number) => (n === 1 ? '1 em falta' : `${n} em falta`),
      gaps: (list: string) => `Em falta: ${list}`,
      externalId: (id: string) => `ID SAFT-AI: ${id}`,
      noExternalId: 'Sem ligação ao SAFT-AI',
      newFirmName: 'Nome do escritório',
      newFirmExternalId: 'ID SAFT-AI (opcional)',
      create: 'Criar',
      save: 'Guardar',
      inboundTitle: 'Entrada de documentos',
      inboundEmail: 'Email de entrada',
      imapHost: 'Servidor IMAP',
      imapUser: 'Utilizador IMAP',
      imapMailbox: 'Pasta',
      whatsappPhoneId: 'WhatsApp phone number ID',
      erpTitle: 'ERP de destino',
      contisEntidade: 'Contis — entidade',
      contisUser: 'Contis — utilizador',
      savePhc: 'Guardar PHC/Contis',
      enableToconline: 'Activar TOConline',
      disableToconline: 'Desactivar TOConline',
      credentialsTitle: 'Credenciais',
      credentialsHint:
        'As credenciais são guardadas mas nunca devolvidas ao navegador — só é possível ver se estão definidas.',
      credSet: 'Definida',
      credUnset: 'Por definir',
      credClear: 'Remover',
      credPlaceholder: 'Nova credencial',
      credImapPassword: 'Palavra-passe IMAP',
      credContisPassword: 'Palavra-passe Contis',
      credWhatsappAppSecret: 'WhatsApp app secret',
      credWhatsappAccessToken: 'WhatsApp access token',
      credWhatsappVerifyToken: 'WhatsApp verify token',
      credToconlineApiSecret: 'TOConline API secret',
    },
    processingFailures: {
      title: 'Nível de serviço',
      subtitle: 'Documentos que falharam, e quantos desses continuam por resolver.',
      classeDocumento: 'A processar o documento',
      classeEnvio: 'A entregar à contabilidade',
      janelas: { '24h': 'Últimas 24h', '7d': 'Últimos 7 dias', '30d': 'Últimos 30 dias' },
      // Medido: as falhas a processar recuperam quase sempre sozinhas e as de entrega não. Daí os
      // dois números — o total sozinho alarmava sem razão, e o que falta resolver sozinho escondia
      // que houve problemas.
      legenda:
        'Lê-se «por resolver / total». Só conta falhas que ficaram registadas: documentos perdidos ' +
        'ANTES de entrarem no sistema não deixam registo, por isso tudo a verde não prova que nada ' +
        'se perdeu — prova que nada falhou depois de entrar.',
    },
    pipelineHealth: {
      title: 'Saúde do pipeline',
      subtitle: 'Contradições que indicam arquivo indevido — devem tender para zero.',
      atcudMistyped: 'QR fiscal mas tipo ≠ fatura',
      multiBuried: 'Multi-documento arquivado sem separar',
      bankstmtMulti: 'Extrato com múltiplos documentos',
      // Esta linha NAO tende para zero: tem um piso conhecido (8 em 10.604 a 01/10, 0,08%).
      // Por isso mostra-se como proporcao e so fica ambar acima do limiar — um mosaico sempre
      // ambar ensina a equipa a ignorar o cartao inteiro.
      atTotalMismatch: 'Total ≠ declarado à AT (via ATCUD)',
    },
    // Invariantes do caminho de entrega. Ao contrário da saúde do pipeline (que mede
    // qualidade de identificação), cada linha aqui é um documento que a entrega deixou
    // de acompanhar em silêncio — sem erro e sem aparecer em lado nenhum.
    routingInvariants: {
      title: 'Invariantes de encaminhamento',
      subtitle: 'Documentos que a entrega deixou de acompanhar em silêncio — devem ser zero.',
      routedNoDestination: 'Encaminhado sem destino registado',
      deliveredNotRouted: 'Entregue mas ainda "em curso"',
      failedWithoutFlag: 'Envio falhado sem sinalização',
    },
    // Ground-truth health (avenue 1 do roadmap no #107): cobertura + concordância por
    // camada de validação. O sinal âmbar no benchmark = o código de medição não está
    // no deploy (a lição do drift de 10/07).
    groundTruth: {
      title: 'Saúde do ground-truth',
      subtitle: 'Cobertura e concordância por camada — o decode do QR é o multiplicador.',
      sections: {
        qr_decode_images: 'Decode QR — imagens (taxa por tamanho)',
        release_ladder: 'Candidatos a entrega (STP)',
        benchmark: 'Benchmark IA-vs-QR — vitalidade',
        benchmark_agreement: 'Concordância IA-vs-QR por campo',
        pendentes_reasons: 'Pendentes por razão',
        corrections_30d: 'Correções humanas (30 dias)',
      },
      metrics: {
        eligible: 'Elegíveis',
        clean_no_flags: 'Limpos (sem flags)',
        clean_qr_grounded: 'Limpos + QR',
        qrcheck_events: 'Eventos qrCheck',
        days_since_last: 'Dias desde o último',
        never: 'nunca',
      },
    },
    correctionRate: {
      title: 'Taxa de correção (por 100 docs)',
      fieldHeader: 'Campo',
      totalRow: 'Total',
      hint: 'A descer = a IA a aprender com as correções (o loop a funcionar).',
    },
    learningLoop: {
      title: 'Ciclo de aprendizagem (hints + regras)',
      hint: 'Cobertura real do loop de correções — o teto é a % de docs de fornecedores com hint.',
      coverage: 'Disparos no período',
      docsSuffix: 'docs identificados',
      ceiling: 'Teto de cobertura',
      ceilingSuffix: 'docs de fornecedores com hint',
      assets: 'Hints · Regras ativas',
      assetsSuffix: 'na tabela hoje',
      correctionsSupply: 'Correções no período',
      refreshPreview:
        'Um refresh das hints AGORA criaria {mint} e apagaria {drop} das existentes (é truncate-and-rebuild).',
      refreshWarning: 'Não correr o refresh sem decidir isto de propósito.',
    },
    shadowAb: {
      title: 'Avaliação sombra (campeão vs. candidato)',
      hint: 'Pares no mesmo documento, adjudicados pelo QR fiscal. A sombra nunca toca na produção.',
      branchVision: 'Visão',
      branchText: 'Texto',
      vsChampion: 'vs.',
      fieldHeader: 'Campo',
      championHeader: 'Campeão (QR)',
      shadowHeader: 'Candidato (QR)',
      discordantHeader: 'Discordantes',
      agreementHeader: 'Acordo (todos)',
      qrPairsSuffix: 'pares QR',
      overviewLine: '{calls} chamadas · {cost} · ok {ok} · p95 {p95}ms',
      fields: {
        supplierNif: 'NIF fornecedor',
        documentType: 'Tipo de documento',
        issueDate: 'Data de emissão',
        totalAmount: 'Total',
        clientNif: 'NIF cliente',
        direction: 'Direção',
      } as Record<string, string>,
    },
    // Ex-Resumo routing-gaps strings, now rendered as the config badge + stuck-doc count on the
    // Pipeline column of Admin › Clientes (the card and the tab are gone).
    routingGaps: {
      docsStuck: 'por entregar',
      reasons: {
        no_pipeline: 'Sem pipeline',
        phc_no_company: 'PHC sem empresa',
        toconline_no_destination: 'TOConline sem destino',
      },
    },
    nav: {
      breadcrumb: 'Administração',
      overview: 'Resumo',
      analytics: 'Analítica',
      carteiras: 'Carteiras',
      clients: 'Clientes',
      consultants: 'Consultores',
      sections: {
        overview: 'Vista',
        config: 'Configuração',
        access: 'Acesso',
      },
    },
    // Ex-Resumo "Definições da plataforma" card, now on Admin › Notificações.
    platformSettings: {
      title: 'Definições da plataforma',
      deadlineDayLabel: 'Dia do prazo IVA',
      deadlineDayHint: 'Dia do mês usado em todos os contadores de prazo (1–28).',
      deadlineDaySaved: 'Prazo atualizado.',
      deadlineDayInvalid: 'Introduza um número entre 1 e 28.',
    },
    clients: {
      title: 'Clientes',
      subtitle: 'Clientes da firma. O NIF define o destinatário TOConline por omissão.',
      createButton: 'Novo cliente',
      filterByCarteira: 'Filtrar por carteira',
      all: 'Todas',
      nifLabel: 'NIF',
      nameLabel: 'Nome',
      carteiraLabel: 'Carteira',
      // Channel/config columns (ex-Resumo "requer atenção", now filterable here).
      emailHeader: 'Emails',
      emailHint: 'Endereços + domínios registados para atribuição por remetente',
      whatsappHeader: 'WhatsApp',
      whatsappHint: 'Telefones do cliente autorizados a enviar por WhatsApp',
      channelWith: 'Configurado',
      channelWithout: 'Sem configuração',
      routingConfigHeader: 'Encaminhamento',
      routingConfigOk: 'Configurado',
      routingConfigMissing: 'Em falta',
      pipelineLabel: 'ERP de destino',
      pipelineOptions: {
        toconline: 'TOConline',
        phc: 'PHC',
        client_software: 'Software do cliente',
        // SAFT-AI auto-created clients whose ERP hasn't been chosen yet.
        unconfigured: 'Por configurar',
      },
      toconlineRecipientLabel: 'Destinatário TOConline',
      toconlineRecipientOverridePlaceholder: 'Opcional — usar apenas se diferente do NIF',
      toconlineRecipientDefault: 'Por omissão: {default}',
      phcContisSectionLabel: 'Configuração PHC (Contis)',
      phcContisSharedCredsHint:
        'As credenciais Contis (entidade, utilizador e palavra-passe) são partilhadas por todas as empresas PHC e estão configuradas no servidor. Aqui define apenas a Empresa.',
      phcContisCompanyLabel: 'Empresa',
      phcContisScanProfileLabel: 'Perfil de digitalização',
      phcContisScanProfileHint: 'Por omissão: Faturas',
      // Client detail subpage (/admin/clients/:id — the SAFT-AI-style "Informações da Empresa").
      aliasesLabel: 'Outros nomes',
      aliasesHint:
        'Outros nomes por que esta empresa aparece — nos documentos e nas pastas TaxDome. Um documento cujo destinatário corresponda a um alias é atribuído a esta empresa. Enter para confirmar.',
      detailSaftaiManaged: 'SAFT-AI',
      detailIdentityLockedHint: 'Identidade gerida no SAFT-AI — editar lá.',
      detailAtCredential: 'Credencial AT (e-Fatura)',
      detailAtCredentialWriteOnly: 'Só escrita — definir através de Editar',
      detailOpenOperations: 'Abrir no Centro de Operações',
      detailOpen: 'Abrir',
      analyticalAccountingLabel: 'Contabilidade analítica',
      analyticalAccountingHint:
        'Ativa a Analítica (categorias analíticas), regras e o assistente "Criar Regra com IA" para este cliente — no portal e nas operações. Sem esta opção, nada da Analítica é visível.',
      assistantEnabledLabel: 'Assistente de documentos (Roberto)',
      assistantEnabledHint:
        'Ativa o Roberto no portal deste cliente: um botão flutuante que abre uma conversa sobre os documentos da empresa ("onde está a fatura do computador?", "quanto gastei em restaurantes este mês?"). Responde sempre indicando os documentos que usou, para o cliente os abrir ou descarregar.',
      manualErpSendLabel: 'Envio manual para o ERP',
      manualErpSendHint:
        'Os documentos deste cliente nunca são enviados automaticamente para o ERP. Ficam em "Por Encaminhar" até serem aprovados um a um em Documentos, com o botão "Enviar para ERP".',
      phcContisCompanyRequired: 'A Empresa Contis é obrigatória quando o pipeline é PHC.',
      efaturaSectionLabel: 'Credenciais e-Fatura (AT)',
      efaturaCredsHint:
        'Subutilizador do Portal das Finanças usado para obter automaticamente as faturas de compra (adquirente) do e-Fatura. Guardadas em cofre no servidor — nunca são apresentadas. Deixe em branco para manter as atuais.',
      efaturaSubuserLabel: 'Subutilizador (número)',
      efaturaSubuserHint: 'O número de ordem do subutilizador AT (ex.: 1). Login = NIF/número.',
      efaturaSubuserPlaceholder: 'ex.: 1',
      efaturaPasswordLabel: 'Palavra-passe do subutilizador',
      efaturaPasswordHint: 'Palavra-passe própria do subutilizador (distinta da senha principal).',
      efaturaPasswordPlaceholder: '•••••• (inalterado)',
      clientSoftwareHint:
        'O cliente utiliza software próprio. Os documentos não serão encaminhados automaticamente.',
      priorityLabel: 'Prioridade',
      priorityOptions: { '1': '1 — Alta', '2': '2 — Média', '3': '3 — Baixa' },
      cadenceLabel: 'Periodicidade',
      cadenceHint:
        'Periodicidade com que o cliente entrega documentos e extratos. Define o período de risco (um cliente trimestral não é penalizado pelos meses intermédios) e os períodos esperados na página Extratos.',
      cadenceOptions: {
        monthly: 'Mensal',
        quarterly: 'Trimestral',
        semiannual: 'Semestral',
        annual: 'Anual',
      },
      ivaRegimeLabel: 'Regime de IVA',
      ivaRegimeHint:
        'Periodicidade da declaração de IVA (mensal, trimestral ou semestral) — quando o IVA é devido. Obrigação fiscal, independente da cadência de entrega. Usada na página IVA.',
      ivaRegimeUnset: '— não definido —',
      ivaRegimeOptions: {
        mensal: 'Mensal',
        trimestral: 'Trimestral',
        semestral: 'Semestral',
      },
      expectedDocsLabel: 'Documentos esperados por mês (manual)',
      expectedDocsHint:
        'Sobrepõe-se ao valor calculado do histórico. Deixe vazio para usar a média histórica por período (ou excluir do risco se não houver histórico).',
      primaryConsultorLabel: 'Consultor principal',
      primaryConsultorNone: 'Por atribuir',
      primaryConsultorHint:
        'Atribuição directa, independente da carteira. Útil para clientes inactivos / dormentes que ainda precisam de seguimento.',
      atPasswordLabel: 'Password Portal AT',
      atPasswordHint:
        'Para integração com o Portal das Finanças (E-fatura). Visível apenas para administradores.',
      atPasswordPlaceholder: '— deixar em branco se não aplicável —',
      taxdomeFolderLabel: 'Pasta TaxDome',
      emailDomainsLabel: 'Domínios de e-mail autorizados',
      emailDomainsHint: 'Adicione domínios sem @ (ex: acme.pt). Enter para confirmar.',
      contactEmailsLabel: 'E-mails do cliente',
      contactEmailsHint:
        'Endereços completos de quem envia documentos deste cliente. Um documento enviado destes emails é atribuído a este cliente. Enter para confirmar.',
      whatsappNumbersLabel: 'Telefones do cliente',
      whatsappNumbersHint:
        'Telefones autorizados a enviar documentos por WhatsApp. Só estes números são aceites. Formato internacional E.164 (ex: +351912345678). Enter para confirmar.',
      whatsappNumbersRequiredFirst:
        'Preencha primeiro os campos obrigatórios (NIF e Nome) para adicionar números.',
      bankAccessLabel: 'Acesso bancário',
      bankAccessHint:
        'Estado do acesso ao extrato bancário deste cliente. Aparece na coluna Extrato.',
      bankAccessHeader: 'Extrato',
      bankAccountsLabel: 'Bancos do cliente',
      bankAccountsHint:
        'Nome de cada banco/conta (ex: Revolut, Novo Banco). Um período só fica completo quando há extrato de todos. Enter para confirmar.',
      bankAccessOptions: {
        none: 'Sem acesso',
        access: 'Com acesso',
        auto: 'Envio automático',
      },
      activeLabel: 'Ativo',
      estadoLabel: 'Estado',
      estadoOptions: {
        ativo: 'Ativa',
        dormente: 'Dormente',
        naoEnvia: 'Não envia documentos',
        inactivo: 'Inativa',
      },
      createTitle: 'Criar cliente',
      editTitle: 'Editar cliente',
      empty: 'Ainda não existem clientes.',
      consultoresHeader: 'Consultores',
      teamLeaderHeader: 'Team Leader',
      consultoresNone: '—',
      dormantLabel: 'Dormente',
      noDocumentsLabel: 'Não envia documentos',
      inactiveLabel: 'Inativa',
      inactiveHint: 'Empresa inactiva — fica fora de todas as análises e do total de empresas.',
      filterByConsultantAll: 'Consultor: todos',
      filterByConsultantNone: 'Sem consultor',
      mainConsultantLabel: 'Consultor principal',
      mainConsultantNone: 'Sem consultor principal',
      additionalConsultantsLabel: 'Outros consultores',
      additionalConsultantsHint: 'Adicione consultores adicionais que trabalhem neste cliente.',
      additionalConsultantsPlaceholder: 'Pesquisar consultor…',
      additionalConsultantsEmpty: 'Sem consultores disponíveis.',
      teamLeadLabel: 'Team Leader',
      teamLeadNone: 'Sem team leader',
      teamLeadHint:
        'Team leader responsável por esta empresa. É por empresa, não por consultor — o mesmo consultor pode ter team leaders diferentes conforme o cliente. Usado nas análises de Risco, IVA e Extratos.',
    },
    consultants: {
      title: 'Consultores',
      subtitle: 'Equipa interna. Provisionamento só por convite.',
      searchPlaceholder: 'Pesquisar nome ou email…',
      inviteButton: 'Convidar consultor',
      editTitle: 'Editar consultor',
      bulkSelected: '{n} seleccionados',
      bulkAssignLabel: 'Atribuir carteira',
      bulkApply: 'Aplicar',
      bulkClear: 'Limpar selecção',
      bulkApplied: '{n} consultores actualizados.',
      bulkError: 'Não foi possível aplicar a actualização.',
      fullNameLabel: 'Nome completo',
      emailLabel: 'Email',
      emailInvalid: 'Email inválido.',
      emailEditHint:
        'Alterar o email muda também a identidade de login: o consultor passa a autenticar-se (magic-link) com o novo endereço.',
      roleLabel: 'Papel',
      roleOptions: {
        consultor: 'Consultor',
        team_lead: 'Team Leader',
        admin: 'Administrador',
      },
      carteiraLabel: 'Carteira atribuída',
      carteiraNone: 'Nenhuma',
      teamLeadLabel: 'Team Leader',
      teamLeadNone: 'Sem team leader',
      clientsHeader: 'Clientes',
      clientsCountSingular: '1 cliente',
      clientsCountPlural: '{n} clientes',
      clientsCountNone: 'Sem clientes',
      assignmentsTitle: 'Clientes atribuídos',
      assignmentsEmptyActive: 'Sem atribuições ativas.',
      assignmentsEmptyAll: 'Sem atribuições registadas.',
      assignmentsShowHistory: 'Mostrar histórico',
      assignmentsHideHistory: 'Esconder histórico',
      assignmentRoleMain: 'Principal',
      assignmentRoleSecondary: 'Secundário',
      assignmentSince: 'Desde {date}',
      assignmentEnded: 'Terminado em {date}',
      activeLabel: 'Ativo',
      empty: 'Ainda não existem consultores.',
      inviteTitle: 'Convidar consultor',
      inviteHintAutomatic:
        'Será enviado um email de convite com link para definir palavra-passe. A conta fica ativa após o primeiro login com MFA.',
      inviteErrorAlreadyExists: 'Já existe uma conta com este email.',
      magicLinkButton: 'Enviar link de acesso',
      magicLinkSent: 'Link de acesso enviado para {email}.',
      magicLinkError: 'Não foi possível enviar o link de acesso.',
    },
    common: {
      createdAt: 'Criado em',
      updatedAt: 'Atualizado em',
      status: 'Estado',
      actions: 'Ações',
    },
    risco: {
      title: 'Configuração de Risco',
      subtitle:
        'Ajuste os limiares que classificam os clientes nas categorias de risco mostradas em /operacoes/risco.',
      modelSectionTitle: 'Como é calculado o risco',
      modelSectionHint:
        'O risco mede ENTREGAS, não volume. Crítico é sempre o cliente que não entregou nada no período — nem documentos, nem extratos — e não depende de nenhum limiar abaixo. As restantes classes derivam da mesma ideia: quanto falta e há quanto tempo.',
      modelRules: [
        'Crítico — sem documentos E sem extratos no período (bloqueado).',
        'Alto — uma corrente inteira em falta (0 documentos, ou nenhum dos extratos devidos) de forma persistente.',
        'Médio — uma corrente inteira em falta só neste período; ou falta a maioria dos extratos devidos; ou regressou depois de um silêncio prolongado.',
        'Baixo — falta uma minoria dos extratos devidos, ou houve silêncio recente já recuperado.',
        'Sem risco — entregou tudo o que era devido, sem falhas recentes.',
      ],
      thresholdsSectionTitle: 'Limiares',
      thresholdsSectionHint:
        'Dois números afinam a escala. Não afetam a classe Crítico, que é sempre a ausência total de entregas.',
      fieldLabels: {
        alto_persistence_months: 'Meses de silêncio → Alto',
        partial_medio_pct: 'Extratos em falta para Médio (%)',
      },
      fieldHints: {
        alto_persistence_months:
          'Meses consecutivos sem nada numa corrente a partir dos quais uma corrente em falta agrava para Alto. Mínimo 1.',
        partial_medio_pct:
          '% dos extratos devidos em falta a partir da qual uma falta parcial agrava de Baixo para Médio. 0–100.',
      },
      resetButton: 'Repor padrões',
      savedAt: 'Guardado às {time}',
    },
    // /admin/novidades — as novidades que escolhemos contar aos clientes. A curadoria é
    // MANUAL por decisão (Nuno, 2026-09-28): não há publicador automático a partir do
    // changelog, e o texto aqui escreve-se para o cliente, não para quem programa.
    novidades: {
      title: 'Novidades para clientes',
      subtitle:
        'Escreva o que mudou em linguagem de cliente e escolha o que vale a pena comunicar. Guardar não avisa ninguém — só Publicar.',
      newButton: '+ Nova novidade',
      editTitle: 'Editar novidade',
      createTitle: 'Nova novidade',
      titleLabel: 'Título',
      titlePlaceholder: 'Ex.: Já pode ver as suas faturas por fornecedor',
      bodyLabel: 'Texto',
      bodyPlaceholder:
        'Explique a melhoria ou a correção em uma ou duas frases, como a explicaria ao cliente ao telefone.',
      bodyHint: 'Sem termos técnicos: o cliente lê isto no portal.',
      mediaLabel: 'Imagem ou vídeo (opcional)',
      mediaPlaceholder: 'https://…',
      mediaHint:
        'Cole um endereço https. Imagens (.png, .jpg, .gif) aparecem no portal; qualquer outro endereço (Loom, YouTube) aparece como link.',
      mediaInvalid: 'O endereço tem de começar por https://',
      statusDraft: 'Rascunho',
      statusPublished: 'Publicada',
      publishedAt: 'Publicada a {date}',
      publish: 'Publicar',
      unpublish: 'Retirar',
      colTitle: 'Novidade',
      colStatus: 'Estado',
      colUpdated: 'Atualizada',
      colActions: 'Ações',
      empty: 'Ainda não escreveu nenhuma novidade.',
      loadFailed: 'Não foi possível carregar as novidades.',
      saveFailed: 'Não foi possível guardar a novidade.',
      publishConfirmTitle: 'Publicar esta novidade?',
      publishConfirmBody:
        'Vai ficar visível no portal de todos os clientes e cada utilizador recebe a notificação com o número na área Novidades.',
      publishConfirmIrreversible:
        'Pode retirá-la depois, mas quem já a tiver lido não volta a ser avisado.',
      publishedCount: 'Publicada. {n} utilizadores notificados.',
      publishedNoRecipients:
        'Publicada. Nenhum utilizador do portal para notificar de momento — fica visível para quem entrar.',
      alreadyPublished: 'Esta novidade já estava publicada.',
      unpublishConfirmTitle: 'Retirar esta novidade?',
      unpublishConfirmBody:
        'Deixa de aparecer no portal e as notificações que gerou são apagadas. Volta a rascunho.',
      deleteConfirmTitle: 'Eliminar esta novidade?',
      deleteConfirmBody: 'Apaga o texto e as notificações que gerou. Não é reversível.',
    },
    notificacoes: {
      title: 'Notificações',
      subtitle: 'Regras de notificação automática e registo de comunicações enviadas.',
      rulesTitle: 'Regras',
      extratoRuleTitle: 'Extratos bancários',
      extratoRuleHint:
        'Notifica automaticamente os clientes cujo extrato do período fechado está em falta. Corre no dia indicado do mês seguinte ao fecho do período.',
      enabledLabel: 'Ativo',
      dayLabel: 'Dia do mês',
      dayHint: 'Dia (1–28) em que o agente verifica e notifica.',
      channelLabel: 'Canal',
      channelEmail: 'Email',
      channelWhatsapp: 'WhatsApp (em breve)',
      savedAt: 'Guardado às {time}',
      logTitle: 'Registo de comunicações',
      logHint: 'Todas as comunicações preparadas para clientes (automáticas e manuais).',
      logEmpty: 'Sem comunicações registadas.',
      colClient: 'Cliente',
      colReason: 'Motivo',
      colChannel: 'Canal',
      colStatus: 'Estado',
      colPeriod: 'Período',
      colCreated: 'Criado',
      colSent: 'Enviado',
      statusPending: 'Pendente',
      statusSent: 'Enviado',
      statusAcked: 'Respondido',
      statusCancelled: 'Cancelado',
      reasonMissingStatement: 'Extrato em falta',
      filterAllStatuses: 'Todos os estados',
    },
  },
  errors: {
    genericSave: 'Não foi possível guardar. Tente novamente.',
    genericLoad: 'Não foi possível carregar os dados.',
    nifTaken: 'Este NIF já existe.',
    carteiraNameTaken: 'Já existe uma carteira com este nome.',
  },
  operations: {
    brand: 'JA Consultores · Centro de Operações',
    risco: {
      title: 'Risco da Carteira',
      windowLabel: 'Janela',
      kpis: {
        totalActive: 'Clientes activos',
        totalActiveFoot: 'na carteira · {n} sem expectativa',
        semDocsFoot: 'sem documentos no período, mas com extratos entregues',
        semExtratosFoot: 'com documentos no período, mas com extratos por entregar',
        criticos: 'Críticos',
        criticosFoot: 'sem documentos e sem extratos no período — contactar já',
        maxConcentration: 'Maior concentração de críticos',
        maxConcentrationEmpty: 'Sem dados suficientes',
      },
      // Presence cards (click-to-filter, the /documentos grammar). Definitions are literal —
      // the tooltips ARE the spec.
      presence: {
        allLabel: 'Todos',
        allDescription: 'Todos os clientes da carteira na Situação seleccionada.',
        critico: 'Críticos',
        criticoDescription:
          'Sem QUALQUER entrega no período: nem documentos, nem extratos bancários. Clientes bloqueados — têm de ser contactados.',
        semDocumentos: 'Docs em falta',
        semDocumentosDescription:
          'Zero documentos no período MAS com extratos bancários entregues — só falta a documentação. Quem não entregou nada conta como Bloqueado, não aqui. Um cliente que enviou algum documento nunca fica em falta; a coluna Esperados (AT) serve apenas de referência.',
        semExtratos: 'Extratos em falta',
        semExtratosDescription:
          'Com documentos entregues MAS a faltar pelo menos um extrato bancário no período (N/M incompleto — mesmo princípio da página Extratos, por banco configurado). Só conta para clientes cujos extratos são enviados pelo cliente — com acesso do escritório ou recolha automática não há nada a cobrar.',
        chipCritico: 'Crítico',
        chipSemDocumentos: 'Docs em falta',
        chipSemExtratos: 'Extratos em falta',
        chipOk: 'OK',
      },
      distributionTitle: 'Distribuição da carteira por risco',
      distributionSubtitle:
        'Proporção de clientes em cada classe de risco de entrega. Crítico = sem documentos e sem extratos no período; as classes abaixo medem quanto falta e há quanto tempo.',
      byConsultantTitle: 'Repartição por consultor',
      byConsultantSubtitle:
        'Apenas o consultor principal. Os secundários aparecem na vista por cliente.',
      byConsultantEmpty: 'Sem consultores principais atribuídos.',
      byTlTitle: 'Repartição por team leader',
      byTlSubtitle: 'Agregado dos consultores que reportam a cada team leader.',
      byTlEmpty: 'Nenhum team leader configurado.',
      clientsTitle: 'Clientes',
      clientsSubtitle: '{n} clientes ordenados por gravidade — os cartões acima filtram a tabela.',
      clientsEmpty: 'Sem clientes para os filtros seleccionados.',
      searchConsultant: 'Pesquisar consultor…',
      searchTeamLead: 'Pesquisar team leader…',
      searchClient: 'Pesquisar cliente…',
      searchEmpty: 'Sem resultados para esta pesquisa.',
      cols: {
        client: 'Cliente',
        consultant: 'Consultor',
        teamLead: 'Team Leader',
        total: 'Total',
        emRisco: 'Em risco',
        criticos: 'Críticos',
        pctRisco: '% em risco',
        worstConsultant: 'Consultor com mais críticos',
        received: 'Documentos',
        extratos: 'Extratos',
        expected: 'Esperados (AT)',
        missedMonths: 'Meses sem entregas',
        riskClass: 'Classe',
        presence: 'Estado',
      },
    },
    riscoDetail: {
      back: 'Voltar a Risco',
      notFound: 'Não foi possível encontrar este cliente. Pode ter saído da janela de análise.',
      openClient: 'Abrir ficha completa do cliente',
      consultant: 'Consultor',
      received: 'Documentos recebidos',
      expected: 'Esperados (AT)',
      deviation: 'Desvio',
      deviationHint: 'Referência — não entra no cálculo do risco.',
      missedMonths: 'Meses sem entregas',
      presenceLabel: 'Entregas no período',
      perMonth: 'docs / período',
      cadenceLabel: 'Periodicidade',
      cadence: { '1': 'Mensal', '3': 'Trimestral', '6': 'Semestral', '12': 'Anual' },
      chartTitle: 'Recebido vs esperado por mês de emissão',
      chartReceived: 'Recebido',
      chartExpected: 'Esperado (média/mês)',
      nextActionTitle: 'Próxima acção',
      noExpectationHint:
        'Este cliente ainda não tem expectativa mensal definida. Defina o valor em "Documentos esperados por mês" no formulário do cliente.',
      howClassifiedTitle: 'Como é calculada esta classe',
      howClassifiedHint:
        'O risco mede entregas. Crítico é sempre a ausência total — sem documentos e sem extratos no período.',
      rulesCritico: 'Crítico — sem documentos e sem extratos no período.',
      rulesAlto: 'Alto — uma corrente inteira em falta há ≥ {n} meses consecutivos.',
      rulesMedio:
        'Médio — uma corrente inteira em falta só neste período, ≥ {p}% dos extratos devidos em falta, ou regresso após ≥ {n} meses de silêncio.',
      rulesBaixo: 'Baixo — falta uma minoria dos extratos devidos, ou houve silêncio recente.',
      rulesSemRisco: 'Sem risco — entregou tudo o que era devido, sem falhas recentes.',
      gapLabel: 'Em falta neste período',
      gapValues: {
        tudo: 'Tudo (documentos e extratos)',
        corrente: 'Uma corrente inteira',
        parcial: 'Parte dos extratos devidos',
        nenhum: 'Nada',
      },
    },
    visaoGlobal: {
      title: 'Analytics',
      subtitle:
        'Distribuição do trabalho pela carteira de clientes — médias, ABC e evolução nos últimos meses.',
      windowLabel: 'Janela',
      kpis: {
        activeClients: 'Clientes activos',
        activeClientsFoot: 'na carteira atual',
        docsReceived: 'Documentos recebidos',
        docsReceivedFoot: 'nos últimos {n} meses',
        meanDocs: 'Média Docs',
        meanDocsFoot: 'documentos por cliente',
        medianDocs: 'Mediana Docs',
        medianDocsFoot: 'por cliente',
        pareto: 'Análise ABC',
        paretoValue: '{n} clientes',
        paretoFoot: 'Classe A · {clients}% da carteira · {docs}% do volume',
      },
      chartTitle: 'Evolução da distribuição documental',
      chartSubtitle: 'Média, mediana e quota da Classe A em cada mês.',
      seriesMean: 'Média',
      seriesMedian: 'Mediana',
      seriesAShare: '% Classe A',
      abcTitle: 'Análise ABC',
      abcSubtitle: 'Classe A: acumulado ≤ 80% · Classe B: 80–95% · Classe C: restantes.',
      abcOpenDrawer: 'Ver ranking completo →',
      abcDocShare: '{pct}% dos docs',
      abcClientShareTail: 'dos clientes',
      drawerTitle: 'Ranking ABC por cliente',
      drawerSubtitle: '{n} clientes ordenados por volume de documentos.',
      drawerCols: {
        client: 'Cliente',
        docs: 'Documentos',
        pct: '% total',
        cumulative: '% acumulado',
        klass: 'Classe',
      },
      issueMonth: {
        title: 'Documentos por data do documento',
        subtitle: 'Por mês de emissão (campo "Data do documento") · {year} até hoje.',
        seriesBars: 'Documentos',
        seriesMean: 'Média / cliente',
        seriesMedian: 'Mediana / cliente',
      },
    },
    meusClientes: {
      title: 'Clientes',
      subtitle:
        'Estado dos clientes que tem atribuídos no mês corrente. Clique numa linha para abrir o detalhe.',
      // #36 / #37 — drilldown chip shown when the page is filtered via
      // ?consultor= or ?team_leader= (typically arriving from /operacoes/risco).
      drilldownConsultor: 'Consultor',
      drilldownTeamLeader: 'Team Leader',
      drilldownClear: 'Limpar filtro',
    },
    notificacoes: {
      title: 'Notificações',
      subtitle:
        'Pontos de atenção apurados a partir dos clientes e documentos do mês corrente. Clique numa linha para abrir o detalhe.',
      inboxTitle: 'Caixa de entrada',
      inboxEmpty: 'Sem notificações.',
      markAllRead: 'Marcar todas como lidas',
      markRead: 'Marcar como lida',
      openDocument: 'Abrir documento',
      documentGone: 'Este documento já não existe — o pedido foi entretanto resolvido.',
      inboxColTitle: 'Pedido',
      inboxColDetail: 'Detalhe',
      inboxColDate: 'Data',
      inboxColActions: 'Ações',
      standingTitle: 'Pontos de atenção',
      empty: 'Tudo em ordem — sem notificações no momento.',
      pendingBadge: 'por rever',
      awaitingBadge: 'à espera',
      kindHeader: 'Tipo',
      clientHeader: 'Cliente',
      priorityHeader: 'Prioridade',
      lastReceivedHeader: 'Última entrega',
      lastReceivedNone: '—',
      kindBankMissing: 'Sem extrato bancário',
      kindErrors: '{n} documento com erro',
      kindErrorsPlural: '{n} documentos com erro',
      kindUnderExpected: 'Abaixo do esperado ({actual} de ~{expected})',
      // Arrumação por temas — os chips de filtro no topo da página.
      themeAll: 'Todas',
      themePedidos: 'Pedidos de clientes',
      themeDocumentos: 'Documentos',
      themeExtratos: 'Extratos',
      themeCorrecoes: 'Correções',
      themeOutros: 'Outros',
      themeFilterLabel: 'Filtrar por tema',
      emptyForTheme: 'Sem notificações neste tema.',
    },
    // Maker-checker review queue (#96 Phase 3b).
    preview: {
      title: 'Documento',
      open: 'Abrir documento',
      openLink: 'Abrir ligação',
      loading: 'A carregar pré-visualização…',
      failed: 'Não foi possível carregar a pré-visualização.',
    },
    // In-app image viewer. Images used to open in a bare browser tab, which fits them to
    // the window and offers no zoom — unreadable for a photographed invoice.
    imageViewer: {
      zoomIn: 'Aproximar',
      zoomOut: 'Afastar',
      reset: 'Repor o tamanho',
      openTab: 'Abrir noutro separador',
      hint: 'Use + e − para aproximar, 0 para repor. Com zoom, arraste para deslocar.',
      failed: 'Não foi possível obter o documento.',
      cannotRender:
        'Não é possível pré-visualizar esta imagem no navegador. Abra-a noutro separador para a descarregar.',
    },
    // Manual crop tool: separate a file that holds several invoices into one
    // document per invoice (multi-invoice auto-crop fallback).
    split: {
      open: 'Separar',
      title: 'Separar documentos',
      instructions:
        'Desenhe um retângulo à volta de cada documento. Cada retângulo torna-se um documento separado.',
      boxCount: '{n} retângulo desenhado',
      boxCountPlural: '{n} retângulos desenhados',
      needTwo: 'Desenhe pelo menos 2 retângulos para separar.',
      submit: 'Separar',
      submitting: 'A separar…',
      done: 'Documentos em separação — aparecem dentro de ~1 minuto.',
      clear: 'Limpar',
      remove: 'Remover',
      error: 'Não foi possível separar o documento',
      loading: 'A carregar documento…',
      failed: 'Não foi possível carregar o documento para separar.',
    },
    minhasCorrecoes: {
      title: 'Minhas Correções',
      subtitle: 'As correções que propôs e o que aconteceu a cada uma.',
      empty: 'Ainda não propôs nenhuma correção.',
      reviewNote: 'Motivo',
      status: {
        pending: 'Pendente',
        approved: 'Aprovada',
        rejected: 'Rejeitada',
      },
    },
    // Sender-validation queue: unregistered emails/numbers that delivered documents,
    // awaiting approve/reject. Replaces the immediate auto-reply and the per-client
    // "Emails observados" section in the admin client form.
    remetentes: {
      title: 'Remetentes',
      subtitle:
        'Endereços de email e números de WhatsApp não registados que enviaram documentos. Aprovar associa o remetente à empresa e processa os documentos retidos; rejeitar dispensa-os e avisa o remetente por email.',
      empty: 'Sem remetentes por validar.',
      channelHeader: 'Canal',
      senderHeader: 'Remetente',
      clientHeader: 'Empresa',
      docsHeader: 'Documentos',
      lastSeenHeader: 'Último envio',
      actionsHeader: 'Ações',
      channelEmail: 'Email',
      channelWhatsapp: 'WhatsApp',
      parkedDocs: (n: number) => (n === 1 ? '1 retido' : `${n} retidos`),
      processedDocs: (n: number) => (n === 1 ? '1 processado' : `${n} processados`),
      sharedBadge: (n: number) => `Enviou para ${n} empresas`,
      sharedHint:
        'Este endereço entregou documentos a várias empresas — confirme a empresa certa antes de aprovar.',
      rejectedStillSending: 'Rejeitado — continua a enviar',
      pickClient: 'Escolher empresa…',
      approve: 'Aprovar',
      reject: 'Rejeitar',
      approveConfirmTitle: 'Aprovar remetente',
      approveConfirm: (sender: string, clients: string) =>
        `Associar ${sender} a ${clients}? Os documentos retidos deste remetente serão processados automaticamente.`,
      approveConfirmShared:
        'Como o endereço fica associado a várias empresas, os documentos retidos entram em Pendentes com sugestões — a empresa certa é confirmada documento a documento.',
      rejectConfirmTitle: 'Rejeitar remetente',
      rejectConfirmEmail: (sender: string) =>
        `Rejeitar ${sender}? Os documentos retidos são dispensados e o remetente recebe um email a informar que o endereço não está registado.`,
      rejectConfirmWhatsapp: (sender: string) =>
        `Rejeitar ${sender}? Os documentos retidos são dispensados. (Números de WhatsApp não recebem aviso automático.)`,
      approvedToast: (n: number) =>
        n === 0
          ? 'Remetente aprovado.'
          : n === 1
            ? 'Remetente aprovado — 1 documento enviado para processamento.'
            : `Remetente aprovado — ${n} documentos enviados para processamento.`,
      rejectedToast: (n: number) =>
        n === 0
          ? 'Remetente rejeitado.'
          : n === 1
            ? 'Remetente rejeitado — 1 documento dispensado.'
            : `Remetente rejeitado — ${n} documentos dispensados.`,
      validationBadge: 'por validar',
      // Remetentes comuns — fornecedores gerais (Revolut, Booking, Microsoft) que entregam
      // para várias empresas. Admitidos para todo o escritório, sem empresa associada: a
      // empresa de cada documento é determinada pelo conteúdo do próprio documento.
      markCommon: 'Marcar como comum',
      commonSuggestion: (n: number) =>
        `Este endereço já entregou para ${n} empresas — provavelmente um remetente comum.`,
      markCommonConfirmTitle: 'Marcar como remetente comum',
      markCommonConfirm: (sender: string) =>
        `Admitir ${sender} para todo o escritório, sem associar a nenhuma empresa? Os documentos deste remetente passam a ser processados e a empresa de cada um é determinada pelo conteúdo do documento.`,
      markCommonConfirmNote:
        'Os documentos cujo conteúdo não permita identificar a empresa ficam em Pendentes à espera de atribuição manual.',
      markedCommonToast: (n: number) =>
        n === 0
          ? 'Remetente marcado como comum.'
          : n === 1
            ? 'Remetente marcado como comum — 1 documento enviado para processamento.'
            : `Remetente marcado como comum — ${n} documentos enviados para processamento.`,
      commonTitle: 'Remetentes comuns',
      commonSubtitle:
        'Endereços admitidos para todo o escritório. Não estão associados a nenhuma empresa — a empresa de cada documento vem do conteúdo do documento.',
      commonEmpty: 'Ainda não há remetentes comuns.',
      commonProcessedHeader: 'Documentos',
      commonClientsHeader: 'Empresas servidas',
      commonAttributedHeader: 'Atribuídos por conteúdo',
      commonDecidedHeader: 'Marcado por',
      unmark: 'Remover',
      unmarkConfirmTitle: 'Remover dos remetentes comuns',
      unmarkConfirm: (sender: string) =>
        `Remover ${sender} dos remetentes comuns? Volta a ficar por validar e os próximos documentos deste endereço ficam retidos.`,
      unmarkedToast: 'Remetente removido dos comuns.',
    },
    revisoes: {
      title: 'Revisões',
      subtitle:
        'Correções propostas por operadores, à espera de aprovação. Aprovar aplica a alteração ao documento e alimenta a IA; rejeitar descarta.',
      empty: 'Nada por rever — sem correções pendentes.',
      approve: 'Aprovar',
      reject: 'Rejeitar',
      rejectConfirm: 'Confirmar rejeição',
      rejectReasonPlaceholder:
        'Motivo da rejeição (ex.: a IA estava certa; o tipo afinal estava correto)…',
      tagLabel: 'Justificação',
    },
    documentos: {
      title: 'Documentos',
      addButton: '+ Adicionar',
      sendToErp: 'Enviar para ERP',
      sendToErpConfirm: (n: number) =>
        n === 1
          ? 'Enviar 1 documento para o ERP do cliente?'
          : `Enviar ${n} documentos para o ERP do cliente?`,
      sendToErpDelay:
        'O envio é feito pelo processo automático no próximo ciclo (até 5 minutos). Os documentos passam a "Encaminhado" quando o ERP confirmar a recepção.',
      filename: 'Documento',
      client: 'Cliente',
      consultant: 'Consultor',
      // Staff-table relabels (2026-08-26 column reorder): the NUMBER column is "Documento", the
      // file column is "Ficheiro". The legacy `filename`/`docNumber` keys keep their old values
      // because the client PORTAL table still uses them.
      colDocumento: 'Documento',
      colFornecedor: 'Fornecedor',
      colFicheiro: 'Ficheiro',
      // "AT" — doc-side e-Fatura reconciliation (same fact as /operacoes/efatura). Renders as a
      // ✓/✗ glyph, not a chip (Diogo 2026-08-26); the full wording lives in the tooltips below.
      situacaoAt: 'AT',
      situacaoAtHint:
        'Conciliação com o e-Fatura da AT: o documento está ligado a uma fatura comunicada pelo fornecedor?',
      atStatus: {
        conciliado: 'Conciliado',
        pendente: 'Pendente',
        // Doc-side wording (Diogo, 2026-08-26): the DOCUMENT exists — what's absent is its AT
        // counterpart. "Em falta" stays only on /operacoes/efatura, where the missing thing IS
        // the document.
        em_falta: 'Sem correspondência',
        sem_dados: 'Sem dados do e-Fatura para este cliente (ainda não sincronizado).',
        nao_aplicavel:
          'Não aplicável — não é uma compra conciliável (tipo não fiscal ou venda própria).',
        via: {
          atcud: 'Conciliado pelo ATCUD (QR fiscal)',
          manual: 'Correspondência confirmada manualmente',
          number: 'Conciliado por fornecedor + número do documento',
        },
      },
      type: 'Tipo',
      state: 'Estado',
      channel: 'Canal',
      received: 'Recebido',
      issueDate: 'Data emissão',
      total: 'Total',
      noClient: '— sem cliente atribuído',
      empty: 'Sem documentos no período seleccionado.',
      loading: 'A carregar…',
      searchPlaceholder: 'Pesquisar por ficheiro, fornecedor ou remetente…',
      docNumber: 'Nº documento',
      stateAny: 'Estado: todos',
      estadoLabel: 'Estado',
      estadoAll: 'Estado: todos',
      channelAny: 'Canal: todos',
      typeLabel: 'Tipo',
      typeAny: 'Tipo: todos',
      clientLabel: 'Cliente',
      clientAll: 'Todos os clientes',
      clientSearchPlaceholder: 'Pesquisar cliente ou NIF…',
      // Filtro "Sem empresa" — documentos que o sistema não conseguiu atribuir a nenhuma
      // empresa. Pergunta pela COLUNA (client_id a nulo) e não pelo motivo de revisão
      // "Sem correspondência cliente": medido a 01/10 há 113 documentos vivos sem empresa
      // e só 86 trazem esse motivo, portanto o filtro de motivo responderia 86 a uma
      // pergunta cuja resposta é 113.
      semClienteLabel: 'Empresa atribuída',
      semClienteOn: 'Sem empresa',
      semClienteAny: 'Todas',
      issueDateLabel: 'Data emissão',
      issueDateAny: 'Qualquer data',
      sentDateLabel: 'Data de envio',
      sentDateAny: 'Qualquer data',
      reviewReasonLabel: 'Razão',
      reviewReasonAny: 'Razão: todas',
      // The two error sub-types are selectable directly in the Estado dropdown (no separate chip).
      estadoErro: {
        documento: 'Erro · Documento',
        envio: 'Erro · Envio',
      },
      // Reason shown UNDER the "Erro" tag in the Estado column (mirrors the Pendentes reason note):
      // a delivery-failure label gives the specific send error; a bare state='error' is a processing
      // failure (the precise cause lives in the document.identify_failed event, not on the row).
      errorReasons: {
        documento: 'Falha ao processar o ficheiro',
        toconline_rejected: 'Rejeitado pelo TOConline',
        routing_failed: 'Falha no Envio para o ERP',
        routing_bounced: 'Email devolvido',
        routing_orphan: 'Envio interrompido',
      },
      // The precise Documento cause, read from the document.identify_failed event (poller
      // IdentifyFailureReason). Shown under the Erro tag when available; falls back to the generic
      // errorReasons.documento for an unmapped/older code.
      identifyFailureReasons: {
        r2_fetch_failed: 'Falha ao obter o ficheiro',
        heic_conversion_failed: 'Imagem ilegível (HEIC)',
        tiff_conversion_failed: 'Imagem ilegível (TIFF)',
        unparseable_pdf: 'PDF ilegível',
        empty_text_layer: 'PDF sem texto',
        encrypted_pdf: 'PDF protegido',
        corrupt_pdf: 'PDF corrompido',
        ai_call_failed: 'Falha na leitura por IA',
        ai_output_invalid: 'Leitura por IA inválida',
        cas_conflict: 'Conflito de processamento',
      } as Record<string, string>,
      reviewReasonOptions: {
        date_anomaly: 'Data impossível',
        vat_implausible: 'IVA implausível',
        low_confidence: 'Confiança baixa',
        unknown_doc_type: 'Tipo desconhecido',
        uncertain_type: 'Tipo por confirmar',
        no_client_match: 'Sem correspondência cliente',
        ambiguous_client_match: 'Correspondência ambígua',
        amount_uncertain: 'Total/IVA inconsistente',
        amount_anomaly: 'IVA superior ao total',
        date_uncertain: 'Data por confirmar',
        date_ambiguous: 'Ordem da data ambígua',
        supplier_unknown: 'Fornecedor desconhecido do registo',
        supplier_name_divergent: 'Nome do fornecedor difere do registo',
        value_not_grounded: 'NIF não consta no documento',
        unverified_field: 'Campo(s) por verificar',
        client_nif_divergent: 'NIF de outro cliente no documento',
        nif_mismatch: 'Dois clientes no documento',
        client_as_supplier: 'Cliente é o fornecedor — venda própria?',
        consumidor_final: 'Consumidor Final — sem NIF do cliente',
        invalid_nif: 'NIF do fornecedor inválido',
        no_metadata: 'Sem dados extraídos — reprocessar antes de enviar',
        needs_split: 'Precisa de separação manual',
        qr_type_mismatch: 'QR fiscal — tipo a confirmar',
        shared_email: 'Email partilhado — escolher empresa',
        email_nif_conflict: 'Email e NIF em conflito — confirmar empresa',
        upload_nif_conflict: 'Cliente do carregamento difere do NIF — confirmar empresa',
        duplicate_atcud: 'Duplicado (mesmo ATCUD)',
        efatura_amount_mismatch: 'Valor difere do declarado na AT',
        zero_total: 'Fatura sem total — confirmar valor',
        unmapped_bank: 'Banco não identificado — atribuir conta',
        toconline_rejected: 'TOConline recusou o ficheiro',
        routing_failed: 'Falha no envio — corrigir e reenviar',
        low_trust_supplier: 'Fornecedor por confirmar',
        link_only: 'Só link — sem ficheiro',
        split_requested: 'Separação em processamento',
        foreign_api_pending: 'Fatura estrangeira — a aguardar API TOConline',
        unauthorised_sender: 'Remetente não autorizado',
        qr_acquirer_mismatch: 'NIF do adquirente (QR) ≠ cliente atribuído',
        supplier_at_mismatch: 'NIF do fornecedor ≠ registo e-Fatura AT',
        receipt_unverified: 'Recibo sem QR — confirmar se é fatura',
        child_client_conflict: 'Documento separado — NIF aponta para outra empresa',
        no_buyer_nif: 'Sem NIF de adquirente — não encaminhável',
        auto_archived_venda: 'Venda do cliente — arquivada, não encaminhada',
        erp_already_filed: 'Já lançado no ERP — não encaminhável',
        auto_archived_erp_manual: 'Já lançado no ERP — arquivado, não encaminhado',
      },
      // "Porquê?" panel (#94): explains WHY the pipeline bucketed / typed / matched
      // a doc, shown BEFORE the operator edits so they can back off a needless change.
      // Keys mirror explainDecision.ts (lib/explainDecision.ts); {placeholders} are
      // single-brace, interpolating the doc's own values. Fuller than the short
      // reviewReasonOptions filter labels above — these are sentences, with the rule.
      why: {
        title: 'Porquê?',
        intro: 'Como a IA decidiu:',
        // bucket — why it sits where it does
        low_confidence: 'Confiança baixa da IA nesta leitura ({confidence}).',
        unknown_doc_type: 'A IA não reconheceu o tipo de documento.',
        uncertain_type:
          'A IA classificou como não-fiscal (Outros/Dispensado) mas com confiança baixa — confirme o tipo antes de arquivar ou dispensar.',
        no_client_match: 'Nenhum NIF ou nome correspondeu a um cliente.',
        no_metadata:
          'Este documento não tem fornecedor, total, número nem data — a leitura automática não produziu nada. Reprocesse ou preencha os dados antes de o enviar para a contabilidade.',
        ambiguous_client_match: 'O nome corresponde a mais do que um cliente.',
        amount_uncertain: 'O total e o IVA não são coerentes — a confirmar.',
        amount_anomaly:
          'O IVA lido é superior ao total do documento — fisicamente impossível; um dos dois valores está errado.',
        date_uncertain: 'A data não pôde ser confirmada.',
        date_ambiguous:
          'A data impressa pode ler-se de duas formas (dia/mês ou mês/dia) e nada no documento o esclarece. Confirme a data correcta.',
        supplier_unknown:
          'Este NIF de fornecedor nunca foi visto no registo — pode ser um fornecedor novo ou uma leitura errada.',
        supplier_name_divergent:
          'O nome impresso não corresponde ao nome registado para este NIF (nem à marca, nem às alcunhas conhecidas).',
        value_not_grounded:
          'O NIF do fornecedor não aparece em lado nenhum do texto do documento — é provável que não tenha sido lido, mas inventado.',
        unverified_field:
          'Um ou mais campos não têm fonte independente disponível para verificação.',
        client_nif_divergent:
          'O documento imprime o NIF de OUTRO cliente do escritório e não o do cliente onde está arquivado — provável atribuição errada.',
        nif_mismatch:
          'O NIF do cliente não aparece no documento (é de outra empresa, do fornecedor, ou é um recibo de consumidor final) — confirme a quem pertence antes de encaminhar.',
        client_as_supplier:
          'O NIF do cliente aparece como FORNECEDOR — isto parece ser uma venda do próprio cliente arquivada como compra. Confirme e corrija o sentido (venda) antes de encaminhar.',
        consumidor_final:
          'Recibo emitido a "Consumidor Final" (sem o NIF do cliente) — pode não ser dedutível. Confirme antes de arquivar/encaminhar.',
        date_anomaly: 'Data impossível: a emissão ({issued}) é posterior à receção ({received}).',
        vat_implausible:
          'IVA improvável: {vat} em {total} ({ratio}) — o total é provavelmente o valor sem IVA.',
        misclassified_fiscal:
          'Está como "Outros" mas tem IVA ({vat}) — pode ser uma fatura mal classificada.',
        invalid_nif:
          'O NIF do fornecedor falha a validação (dígito de controlo) — provável leitura errada.',
        duplicate_atcud:
          'Já existe um documento anterior com o mesmo ATCUD — é a mesma fatura recebida duas vezes. O original é que encaminha; confirme e dispense este duplicado.',
        efatura_amount_mismatch:
          'O total lido neste documento não coincide com o que o fornecedor declarou à AT para a mesma fatura. Confirme qual é o valor certo antes de arquivar/encaminhar — a declaração do fornecedor é a fonte independente.',
        zero_total:
          'Não foi possível ler o total desta fatura. O total é o único campo de que a contabilidade não pode prescindir, pelo que o documento fica retido até alguém o confirmar — em vez de seguir para o ERP sem valor.',
        unmapped_bank:
          'É um extrato bancário mas o banco não corresponde a nenhuma conta configurada para este cliente — atribua a conta certa (ou adicione-a na ficha do cliente).',
        toconline_rejected:
          'O TOConline recusou este ficheiro (tipo inválido — ex.: talão a consumidor final sem NIF, documento mal atribuído, ou incompleto). Veja o motivo exacto no Registo de envios: corrija ou reatribua ao cliente/NIF certo e reenvie, ou dispense se não for uma despesa desta empresa. (Os duplicados já vão automaticamente para o Arquivo Fiscal.)',
        routing_failed:
          'O envio para o sistema de contabilidade (Contis/TOConline) falhou — reveja o documento/configuração e use Reenviar para tentar de novo.',
        qr_type_mismatch:
          'Tem QR fiscal válido (é uma fatura/recibo) mas a IA classificou-o como não-fiscal — confirme o tipo antes de arquivar.',
        shared_email:
          'O email do remetente é partilhado por vários clientes — escolha a empresa certa no editor.',
        unauthorised_sender:
          'O email/número do remetente não está registado no sistema — confirme e adicione à whitelist, ou contacte o cliente.',
        qr_acquirer_mismatch:
          'O QR fiscal indica um NIF de adquirente válido que NÃO é o do cliente atribuído — o documento pode pertencer a outra entidade (ex.: fatura pessoal atribuída à empresa com nome parecido). Confirme a empresa (sugestões no editor) antes de encaminhar.',
        supplier_at_mismatch:
          'O e-Fatura da AT regista, para este cliente, uma compra com a mesma data e o mesmo total mas com OUTRO fornecedor — e o NIF extraído, embora válido, não coincide. Confirme o fornecedor no documento original antes de encaminhar.',
        child_client_conflict:
          'Este documento foi separado de um lote atribuído a uma empresa, mas o NIF do próprio documento resolve para OUTRA empresa da carteira. Antes, o sistema mudava a empresa em silêncio; agora fica retido para confirmar — lotes com documentos de várias empresas são raros, mas entregar um documento na contabilidade errada não se desfaz. Confirme a empresa correta no editor.',
        receipt_unverified:
          'Documento fotografado/digitalizado, sem QR fiscal legível, que a IA classificou como recibo — é a combinação onde uma fatura mal classificada pode ir parar ao Arquivo Outros sem ninguém dar por isso. Confirme o tipo: se for fatura, corrija e encaminhe; se for mesmo recibo, arquive.',
        no_buyer_nif:
          'Documento sem NIF de adquirente válido (consumidor final) — nunca é encaminhado para a contabilidade, mesmo depois de reatribuído. Dispense-o ou arquive-o se não for dedutível.',
        auto_archived_venda:
          'O QR fiscal confirma que foi o próprio cliente a EMITIR este documento — é uma venda, não uma despesa. Fica arquivada no Arquivo Fiscal e nunca é encaminhada para a contabilidade: a entrada <NIF>@my.toconline.pt é só para despesas/compras, e no PHC uma venda seria lançada como compra. Lance-a pelo módulo de vendas/faturação (ou via SAF-T).',
        erp_already_filed:
          'Marcado como "Já processado no ERP" no carregamento — a equipa já o lançou manualmente no TOConline/PHC. Nunca é encaminhado (um segundo envio duplicaria o documento na contabilidade do cliente), mesmo que seja reatribuído ou restaurado do Arquivo.',
        auto_archived_erp_manual:
          'Já lançado manualmente no ERP pela equipa (marcado no carregamento) — catalogado e arquivado no Arquivo Digital, sem envio para o TOConline/PHC.',
        whatsapp_shared_number:
          'O número de WhatsApp do remetente está registado em vários clientes — o sistema não sabe a qual pertence. Escolha a empresa certa (sugestões no editor).',
        email_nif_conflict:
          'O remetente aponta para um cliente mas o NIF no documento aponta para outro — confirme a empresa.',
        needs_split:
          'O ficheiro contém várias faturas e a separação automática não teve confiança suficiente — use "Separar documentos".',
        low_trust_supplier:
          'O fornecedor foi identificado a partir de correções ainda pouco confirmadas — confirme o fornecedor.',
        link_only:
          'O e-mail trazia apenas uma ligação (sem anexo) — abra o link e descarregue o documento.',
        split_requested:
          'Separação manual pedida — o corte corre automaticamente no próximo ciclo (±5 min).',
        multi_doc:
          'O ficheiro contém vários documentos/faturas (não foi possível separá-los automaticamente).',
        outros: 'Tipo não fiscal — vai para Outros, não é encaminhado.',
        link_pending: 'Apenas uma ligação no e-mail (sem ficheiro anexado) — falta descarregar.',
        auto_archived_reference:
          'Documento de referência (passe/extrato/comprovativo) — arquivado automaticamente, não precisa de revisão.',
        auto_archived_spreadsheet:
          'Folha de cálculo — arquivada automaticamente para classificação manual.',
        // client — how the client was resolved
        matched_by_nif: 'Cliente identificado pelo NIF no documento.',
        matched_by_nif_corrected:
          'NIF corrigido (falhou o dígito de controlo, recuperado por aproximação) — a confirmar.',
        matched_by_alias: 'Cliente identificado pelo nome/alias.',
        alias_ambiguous: 'O nome corresponde a vários clientes — a desambiguar.',
        preserved: 'Cliente já estava atribuído (mantido).',
        no_match: 'Sem cliente identificado.',
        // field provenance — trusted QR vs AI read
        field_from_qr: 'Campo "{field}" lido do QR fiscal (fiável).',
        // routing — why it did / didn't forward
        routing_phc_missing_credentials: 'PHC sem empresa configurada — não encaminha.',
        routing_not_bookkeeping_material: 'Não é material de contabilidade — não encaminha.',
        routing_missing_client: 'Sem cliente — não encaminha.',
        routing_client_software_no_routing: 'Cliente sem encaminhamento configurado.',
        routing_link_pending: 'Só link (sem ficheiro) — não encaminha.',
        routing_phc_invalid_login: 'Erro PHC: login inválido.',
        routing_phc_invalid_entidade: 'Erro PHC: entidade inválida.',
        routing_phc_no_permission: 'Erro PHC: sem permissão.',
        routing_phc_generic_error: 'Erro PHC ao encaminhar.',
      },
      // Multi-documento: a single file holding several invoices that we could not
      // auto-split (one-page batch or image). Tagged, filterable, not Pendente.
      multiDocOption: 'Multi-documento',
      multiDocTag: 'Multi-Doc',
      multiDocTagTitle:
        'Este ficheiro contém vários documentos/faturas (não foi possível separar automaticamente).',
      pendenteReasonsPrefix: 'Pendente — motivo:',
      showingFirstN: 'A mostrar os primeiros {n} resultados.',
      totalCount: '{n} no total',
      loadedCount: 'A mostrar {loaded} de {total}',
      loadMore: 'Carregar mais',
      loadingMore: 'A carregar…',
      typeOptions: {
        invoice: 'Fatura',
        credit_note: 'Nota de Crédito',
        debit_note: 'Nota de Débito',
        receipt: 'Recibo',
        bank_statement: 'Extrato',
        transfer_receipt: 'Comprovativo TRF',
        travel_expense: 'Despesa viagem',
        state_payment: 'Pagamento Estado',
        recibo_vencimento: 'Recibo Vencimento',
        mapa_quilometros: 'Mapa Quilómetros',
        ajudas_custo: 'Ajudas de Custo',
        purchase_order: 'Nota encomenda',
        transport_pass: 'Passe',
        meal_voucher_topup: 'Cartão refeição',
        financial_statement: 'Balanço/DR',
        email_thread: 'Email',
        signature_image: 'Imagem assinatura',
        back_of_invoice: 'Verso fatura',
        download_link: 'Ligação download',
        other: 'Outro',
      },
    },
    upload: {
      alreadyInErpLabel: 'Já processado no ERP',
      alreadyInErpHint:
        'Os documentos são catalogados e arquivados, mas não são enviados para o TOConline/PHC — evita duplicados quando a equipa já os lançou manualmente.',
      title: 'Adicionar documentos',
      subtitle:
        'Arraste ficheiros, ZIPs (incluindo exportações do TaxDome) ou clique para escolher.',
      dropHere: 'Largue os ficheiros aqui',
      dropHint: 'PDF, imagens (JPG/PNG/WEBP), Excel (XLSX/XLS) ou ZIPs · até 25 MB por ficheiro',
      pickFile: 'Escolher ficheiros',
      selectFolder: 'Ou selecionar uma pasta (com subpastas)',
      clientLabel: 'Cliente',
      clientPlaceholder: '— Selecione cliente —',
      clientAuto: '— Detetar automaticamente —',
      taxdomeDetected: 'Detectado pelo nome da pasta: {name}',
      taxdomeMatched: '→ associado a "{client}"',
      taxdomeUnmatched: 'Sem cliente correspondente — escolha manualmente.',
      filesQueued: '{n} ficheiros prontos a enviar',
      uploadAll: 'Enviar tudo',
      uploading: 'A enviar…',
      done: 'Concluído',
      reset: 'Limpar',
      close: 'Fechar',
      parsing: 'A analisar ZIP…',
      zipParseFailed: 'Não foi possível abrir o ZIP. Verifique o ficheiro.',
      missingClient: 'Escolha um cliente antes de enviar.',
      empty: 'Ainda não largou nenhum ficheiro.',
      statusPending: 'Pendente',
      statusUploading: 'A enviar',
      statusUploaded: 'Enviado',
      statusDuplicate: 'Duplicado',
      statusSkipped: 'Ignorado',
      statusError: 'Erro',
    },
    sidebar: {
      brandName: 'JA Consultores',
      brandSub: 'Centro de operações',
      // #28 — "Visão Global" renamed to "Analytics" (route also moved
      //       from /operacoes to /operacoes/analytics).
      // #27 — firm-wide overview restored as "Visão Geral" next to it.
      analyticsOps: 'Analytics',
      visaoGeral: 'Visão Geral',
      risco: 'Risco',
      meusClientes: 'Clientes',
      documentos: 'Documentos',
      revisoes: 'Revisões',
      minhasCorrecoes: 'Minhas Correções',
      remetentes: 'Remetentes',
      admin: 'Admin',
      extratos: 'Extratos',
      efatura: 'e-Fatura',
      iva: 'IVA',
      saftAi: 'SAFT AI',
      reconciliation: 'Reconciliação',
      notifications: 'Notificações',
      // #25 — sr-only label for the rolling-24h "Novos" badge on Documentos.
      novosBadge: 'novos nas últimas 24h',
      // legacy keys kept for backwards-compat with primitives.test.tsx and
      // other still-referenced strings; remove once those tests migrate.
      viewSection: 'Vista',
      byClient: 'Por cliente',
      analytics: 'Analítica',
      toolsSection: 'Ferramentas',
      accessSection: 'Acesso',
      allClients: 'Todos os clientes',
    },
    topbarAlert: {
      daysToDeadline: '{days} dias para o prazo do dia {day}',
      missingBankStatements: '{count} clientes sem extrato bancário',
      separator: ' — ',
      deadlineReached: 'Prazo do dia atingido',
    },
    tiles: {
      openClients: 'Clientes em aberto',
      openClientsSub: 'de {total} na carteira',
      urgent: '{count} urgentes',
      inProgress: '{count} em progresso',
      docsReceived: 'Docs recebidos',
      docsReceivedLabel: 'recebidos',
      expectedSeparator: 'Esperados',
      expectedUnknown: '—',
      ofExpected: 'do\nesperado',
      stateBreakdownTitle: 'Estado dos documentos — de {total} recebidos',
      classified: 'Classificados',
      submitted: 'Submetidos',
      pending: 'Pendentes',
      errors: 'Erros / ilegíveis',
    },
    filters: {
      all: 'Todos',
      open: 'Em aberto',
      withErrors: 'Com erros',
      done: 'Concluídos',
      statusLabel: 'Estado',
      statusAll: 'Todos',
      statusUrgent: 'Urgente',
      statusNoDocs: 'Sem documentos',
      statusInProgress: 'Em progresso',
      clienteLabel: 'Cliente',
      clienteAll: 'Todos',
      clienteSearchPlaceholder: 'Pesquisar cliente ou NIF…',
      periodLabel: 'Período',
      monthAll: 'Mês ▾',
      monthCurrent: 'Mês atual',
      channelLabel: 'Canal',
      channelAll: 'Todos',
      consultorLabel: 'Consultor',
      consultorAll: 'Todos',
      teamLeaderLabel: 'Team Leader',
      teamLeaderAll: 'Todos',
      classLabel: 'Classe',
      classAll: 'Todas',
      priorityLabel: 'Prioridade',
      priorityAll: 'Todas',
      priorityOptions: { '1': '1 — Alta', '2': '2 — Média', '3': '3 — Baixa' },
      searchClient: 'Pesquisar cliente...',
      clearAll: 'Limpar filtros',
    },
    toolbar: {
      columns: 'Colunas',
      columnsTitle: 'Mostrar colunas',
      filters: 'Filtros',
      filtersTitle: 'Mostrar filtros',
      lockedHint: 'Sempre visível',
      exportXlsx: 'Exportar',
      exporting: 'A exportar…',
      exportTitle: 'Exportar para Excel (.xlsx) com os filtros atuais',
      exportEmpty: 'Sem linhas para exportar',
      exportCapped:
        'A exportação foi limitada às primeiras {n} linhas — alguns documentos podem ter ficado de fora. Restrinja os filtros para exportar tudo.',
    },
    table: {
      client: 'Cliente',
      total: 'Total',
      received: 'Recebidos',
      classified: 'Classificados',
      submitted: 'Submetidos',
      errors: 'Erros',
      bankStatement: 'Extrato',
      channel: 'Canal',
      classe: 'Classe',
      priority: 'Prioridade',
      consultor: 'Consultor',
      teamLead: 'Team Leader',
      companyStatus: 'Situação',
      statusActive: 'Ativo',
      statusDormant: 'Dormente',
      deadline: 'Prazo',
      nifLabel: 'NIF',
      statementReceived: 'Recebido',
      statementMissing: 'Em falta',
      statementPending: 'Pendente',
      bankAccessYes: 'Com acesso',
      bankAccessNo: 'Sem acesso',
      bankAccessAuto: 'Envio automático',
      empty: 'Nenhum cliente visível.',
      loading: 'A carregar…',
    },
    channels: {
      imap: 'Email',
      dashboard_upload: 'Upload',
      whatsapp: 'WhatsApp',
      drop_folder: 'Drop folder',
      none: '—',
    },
    deadline: {
      ok: 'Concluído',
      warn: '{days} dias',
      late: '{days} dias',
      labelDaysLeft: '{days} dias',
    },
    onboarding: {
      title: 'Onboarding',
      // The check's own label — it states what it is, so it needs no tooltip to be understood.
      cardMeetingDone: 'Reunião efetuada',
      dropHere: 'Largue aqui',
      emptyColumn: 'Vazio',
      stageSaveFailed: 'Não foi possível mover o cliente.',
      clientLabel: 'Cliente',
      clientSearchPlaceholder: 'Procurar cliente…',
      consultorLabel: 'Consultor',
      teamLeadLabel: 'Team Leader',
      situacaoLabel: 'Situação',
      situacaoAtivo: 'Ativo',
      situacaoNaoEnvia: 'Não envia documentos',
      situacaoDormente: 'Dormente',
      searchPlaceholder: 'Procurar cliente ou NIF…',
      meetingMark: 'Marcar reunião',
      meetingUnmark: 'Desmarcar reunião',
      meetingSaveFailed: 'Não foi possível guardar a reunião.',
      // The check stamps today; this is how you correct it to the day the meeting actually was.
      meetingDateLabel: 'Data da reunião',
      // "Meeting held" and "logged into the portal" are deliberately separate questions. A client
      // who registers during the meeting itself satisfies both, and neither answers for the other.
      reuniaoLabel: 'Reunião',
      reuniaoHoje: 'Feita hoje',
      reuniaoCom: 'Com reunião',
      reuniaoSem: 'Sem reunião',
      // Ativado is derived from the client's first portal login (2026-09-16), so the column says
      // so — a column that refuses every drop and does not explain itself reads as broken.
      autoColumnBadge: 'Auto',
      autoColumnEmpty: 'Nenhum cliente nesta fase.',
      autoColumnHint:
        'Fase automática: o cliente entra aqui quando acede ao portal pela primeira vez.',
      cardActivatedAt: 'Entrou no portal',
    },
    efatura: {
      backToIndex: 'Voltar a e-Fatura',
      // Shared by every supplier picker — a supplier is looked up by name when you know who you
      // want, and by NIF when you are working from a document that only carries the number.
      sortOrderLabel: 'Ordenar',
      sortByName: 'Nome',
      sortByNif: 'NIF',
      title: 'e-Fatura',
      pickClient: 'Cliente',
      selectClient: 'Escolha um cliente para ver as faturas do e-Fatura.',
      loading: 'A carregar faturas do e-Fatura\u2026',
      // The invoice query is período-scoped, so "nada aqui" almost always means "wrong period"
      // rather than "this client has nothing" — say so, or the page reads as broken.
      none: 'Sem faturas do e-Fatura neste período.',
      // Shown while the manual refresh is pulling from the AT portal. Names the remote system,
      // because this is the one wait in the app that is not our own database.
      fetchingTitle: 'A obter faturas do Portal das Finanças…',
      fetchingHint: 'A ligação ao e-Fatura demora alguns segundos.',
      fetchFailed: 'Não foi possível obter este período do e-Fatura:',
      lastSync: 'Última sincronização: {when}',
      refresh: 'Atualizar',
      refreshing: 'A atualizar…',
      neverSynced: 'Ainda não sincronizado.',
      count: 'Faturas',
      suppliers: 'Fornecedores',
      totalValue: 'Valor total',
      colSupplier: 'Fornecedor',
      colType: 'Tipo',
      colDate: 'Data',
      colBase: 'Base',
      colIva: 'IVA',
      colTotal: 'Total',
      colAtcud: 'ATCUD',
      // "Documento" = the invoice's own number; "Situação" = OUR verdict on whether we received
      // it. The AT portal's own status keeps a distinct label so the two are never confused.
      colStatus: 'Situação',
      colSituacaoAt: 'Situação (AT)',
      colDoc: 'Documento',
      missing: 'Sem documento',
      // Chip labels + their "nothing selected" previews. Cliente is a SCOPE here (the page can't
      // render without one), so it has no "todos" preview.
      clientSearchPlaceholder: 'Pesquisar cliente ou NIF…',
      docStatusLabel: 'Situação',
      docStatusAny: 'Todos',
      supplierLabel: 'Fornecedor',
      supplierAll: 'Todos',
      supplierSearchPlaceholder: 'Pesquisar fornecedor ou NIF…',
      typeLabel: 'Tipo',
      typeAny: 'Todos',
      searchPlaceholder: 'Pesquisar fornecedor, nº ou ATCUD…',
      invoiceOne: 'fatura',
      invoiceMany: 'faturas',
      noneForFilters: 'Nenhuma fatura corresponde aos filtros.',
      truncatedWarning:
        'Foram carregadas as primeiras {count} faturas deste período — a vista está incompleta. Escolha um período mais curto.',
      docsTruncatedWarning:
        'Este cliente tem demasiados documentos para reconciliar de uma vez; "Em falta" pode estar sobrestimado.',
      // "Conciliado" (not "Recebido") — the same word the Documentos table's Situação AT column
      // uses; the two pages state one reconciliation fact from opposite ends.
      reconMatched: 'Conciliado',
      reconMissing: 'Em falta',
      reconPending: 'Pendente',
      reconAnnulled: 'Anulado',
      reconAnnulledTitle:
        'A AT indica que o emitente anulou este documento. Não há fatura para receber — não conta para Em falta nem para o total do período.',
      reconMissingTitle: 'Fatura no e-Fatura sem documento correspondente recebido na app.',
      reconPendingTitle:
        'Encontrámos um documento candidato, mas os dados não coincidem totalmente. Clique para confirmar ou rejeitar a correspondência.',
      reconViaAtcud: 'Documento correspondente encontrado por ATCUD.',
      reconViaNumber:
        'Documento correspondente encontrado por fornecedor e número do documento, com data ou valor coincidente.',
      reconViaManual: 'Correspondência confirmada manualmente.',
      // NIF do fornecedor divergente (leitura errada), mas nome, data e total coincidem.
      reconViaNameDateTotal:
        'Documento correspondente encontrado por nome do fornecedor, data e total (o NIF lido não coincide).',
      reconOpenDoc: 'Clique para abrir o documento.',
      // Click-to-filter status cards (same visual grammar as /operacoes/documentos). Anuladas
      // have NO card — chegam-se pelo filtro Situação, como os Dispensados nos documentos.
      cardAllLabel: 'Todas',
      cardAllDescription: 'Todas as faturas comunicadas à AT neste período.',
      cardMatchedDescription:
        'Faturas com documento correspondente confirmado — por ATCUD, por número com data ou valor coincidente, ou validado manualmente.',
      cardPendingDescription:
        'Faturas com um documento candidato cujos dados não coincidem totalmente. Precisam de validação humana: confirme ou rejeite a correspondência.',
      cardMissingDescription:
        'Faturas comunicadas à AT sem qualquer documento correspondente recebido na app — o sinal de valor desta página.',
      // Manual reconciliation split view — documents without match ⇄ invoices without document.
      reconSplit: {
        toggle: 'Reconciliação manual',
        toggleExit: 'Voltar à lista',
        hint: 'Selecione um documento à esquerda e uma fatura à direita para os corresponder manualmente. Apenas documentos de fornecedores portugueses.',
        docsTitle: 'Documentos sem correspondência',
        docsSubtitle: 'Documentos recebidos no período sem fatura e-Fatura associada.',
        docsEmpty: 'Todos os documentos do período têm correspondência.',
        invoicesTitle: 'Faturas e-Fatura sem documento',
        invoicesSubtitle: 'Comunicadas à AT no período, sem documento recebido.',
        invoicesEmpty: 'Sem faturas em falta no período.',
        colSupplier: 'Fornecedor',
        colNumber: 'Número',
        colDate: 'Data',
        colTotal: 'Total',
        viewDoc: 'Ver',
        pairPickBoth: 'Selecione um de cada lado',
        pairPickOther: 'Falta selecionar o outro lado',
        pairReady: 'Par pronto a confirmar',
        confirm: 'Confirmar correspondência',
        confirming: 'A guardar…',
        confirmFailed: 'Não foi possível guardar:',
      },
      review: {
        title: 'Validar correspondência',
        intro:
          'Os dados abaixo não coincidem totalmente. Confirme se a fatura da AT e o documento recebido são o mesmo documento.',
        invoiceSide: 'Fatura (AT)',
        docSide: 'Documento recebido',
        fieldNumber: 'Número',
        fieldDate: 'Data',
        fieldTotal: 'Total',
        viewDoc: 'Ver documento',
        confirm: 'Confirmar correspondência',
        reject: 'Rejeitar',
        saving: 'A guardar…',
        failed: 'Não foi possível guardar a validação:',
      },
      overview: {
        subtitle: 'Faturas comunicadas à AT por cliente, cruzadas com os documentos recebidos.',
        // Two filters only: which CLIENTES, and which FORNECEDOR they bought from.
        filterCompany: 'Clientes',
        filterSupplier: 'Fornecedor',
        companySearchPlaceholder: 'Procurar cliente…',
        supplierSearchPlaceholder: 'Escreva o nome ou NIF do fornecedor…',
        supplierHintEmpty: 'Escreva para procurar entre todos os fornecedores.',
        supplierHintTop: 'A mostrar os fornecedores com mais faturas. Escreva para procurar.',
        supplierHintMore: 'Só os primeiros resultados. Refine a pesquisa se não encontrar.',
        colClient: 'Cliente',
        colCount: 'Faturas',
        colSuppliers: 'Fornecedores',
        colTotal: 'Valor total',
        colPending: 'Pendentes',
        colMissing: 'Sem documento',
        colLastSync: 'Última consulta AT',
        neverSynced: 'Nunca',
        syncError: 'A última consulta falhou.',
        noCreds: 'Sem credenciais AT configuradas.',
        update: 'Atualizar',
        updating: 'A atualizar…',
        searchPlaceholder: 'Pesquisar cliente ou NIF…',
        loading: 'A carregar clientes…',
        none: 'Nenhum cliente com dados do e-Fatura ou credenciais AT configuradas.',
        noneForSearch: 'Nenhum cliente corresponde aos filtros.',
        rowOne: 'cliente',
        rowMany: 'clientes',
      },
    },
    reconciliation: {
      title: 'Reconciliação',
      subtitle: 'Cruzamento entre movimentos bancários e documentos de despesa.',
      placeholder: 'Em desenvolvimento — disponível na Fase 2.',
      placeholderHint:
        'Esta área irá listar diferenças entre os movimentos do extrato e as faturas/recibos correspondentes, com sugestões de correspondência automática.',
      disabled: 'A reconciliação bancária está desativada.',
      pickClient: 'Cliente',
      loading: 'A carregar reconciliação…',
      autoMatched: 'Correspondências automáticas',
      unmatchedTx: 'Movimentos sem documento',
      unmatchedDocs: 'Documentos sem movimento',
      colDoc: 'Documento',
      colSupplier: 'Fornecedor',
      colTx: 'Movimento',
      colAmount: 'Valor',
      colDate: 'Data',
      colScore: 'Confiança',
      colOrigin: 'Origem',
      originManual: 'Confirmado',
      originAuto: 'Automático',
      confirm: 'Confirmar',
      reject: 'Rejeitar',
      // Derived-matcher vocabulary (milestone C).
      pendingTitle: 'A precisar de decisão',
      pendingHint:
        'Vários documentos batem certo com o mesmo movimento (ou vários movimentos com o mesmo documento). Escolha qual corresponde.',
      nonePending: 'Nada a decidir.',
      choose: 'É este',
      kpiMatched: 'Conciliados',
      kpiPending: 'Por decidir',
      kpiMissing: 'Sem documento',
      kpiInflows: 'Entradas',
      // Worklist cards (design "Reconciliação Bancária", 2026-09-24).
      cardMovsTitle: 'Movimentos sem documento',
      cardMovsDesc: 'Pagamentos no extrato sem fatura associada.',
      cardDocsTitle: 'Documentos por justificar',
      cardDocsDesc: 'Documentos que não aparecem pagos no banco — indique como foram pagos.',
      cardDoneTitle: 'Conciliados',
      cardDoneDesc: 'Movimentos já com documento.',
      // Two-panel association.
      panelDocs: 'Documentos',
      panelMovs: 'Extrato bancário',
      panelDocsLegend: '{n} sem pagamento associado',
      panelMovsLegend: '{n} movimentos sem documento',
      selectedSuffix: '{n} seleccionado · {total}',
      selectedSuffixPlural: '{n} seleccionados · {total}',
      pickDocsFirst: 'Escolha primeiro um ou mais documentos.',
      multiHint:
        'Pode escolher vários documentos e vários movimentos ao mesmo tempo — uma fatura paga em prestações, um pagamento que liquida várias faturas, ou várias faturas pagas em várias transferências.',
      suggested: 'sugerido',
      // Live checks.
      checksTitle: 'Verificação',
      checkValor: 'Valor total',
      checkDatas: 'Datas',
      checkDescritivo: 'Descritivo',
      checkOk: 'coincide',
      checkFail: 'não coincide',
      checkDiff: 'Diferença {v}',
      associateN: 'Associar {docs} ↔ {movs}',
      associated: 'Associado · {docs} ↔ {movs} · {total}',
      // Justification.
      justifyCta: 'Justificar pagamento',
      justifyTitle: 'Como foi pago?',
      justifyHint: 'Sem movimento no banco. Indique o meio de pagamento.',
      justifiedTitle: 'Pagos por outra via',
      justifiedAs: 'Pago: {method}',
      justifyUndo: 'Repor',
      justified: 'Justificado · {n} · {method}',
      methodCartaoPessoal: 'Cartão de crédito pessoal',
      methodDinheiro: 'Dinheiro',
      methodOutraConta: 'Outra conta bancária da empresa',
      methodSocioGerente: 'Pago por sócio / gerente',
      methodOutro: 'Outro — explicar ao contabilista',
      inflowsTitle: 'Entradas',
      inflowsHint:
        'Dinheiro recebido. Fica registado, mas não pedimos documento — as faturas de venda do cliente normalmente não passam por aqui.',
      noneInflows: 'Sem entradas no período.',
      derivedNote:
        'As correspondências são recalculadas a cada abertura; apenas as suas decisões ficam guardadas.',
      reviewFailed: 'Não foi possível guardar a decisão. Tente novamente.',
      none: 'Sem correspondências.',
      noneTx: 'Sem movimentos por reconciliar.',
      noneDocs: 'Sem documentos por reconciliar.',
      selectClient: 'Escolha um cliente para ver a reconciliação.',
      manualHint: 'Selecione um movimento e um documento para os associar manualmente.',
      associate: 'Associar',
      associating: 'A associar…',
      clearSelection: 'Limpar seleção',
      manualMatched: 'Movimento e documento associados.',
      manualAlreadyMatched: 'Um dos itens já foi entretanto reconciliado — listas atualizadas.',
      manualFailed: 'Não foi possível associar. Tente novamente.',
    },
    iva: {
      title: 'IVA',
      subtitle:
        'Planeamento da declaração de IVA: documentos fiscais por estado + estado do extrato.',
      colClient: 'Cliente',
      colConsultor: 'Consultor',
      colRegime: 'Regime',
      colEnviado: 'Enviado',
      colPorEncaminhar: 'Por Encaminhar',
      colACorrigir: 'A corrigir',
      colExtrato: 'Extrato',
      regimeFilterLabel: 'Regime IVA',
      regimeMensal: 'Mensal',
      regimeTrimestral: 'Trimestral',
      regimeSemestral: 'Semestral',
      regimeUnset: '—',
      extratoOk: 'Completo',
      extratoAuto: 'Automático',
      extratoNa: '—',
      empty: 'Sem clientes para mostrar.',
      loadError: 'Erro a carregar dados de IVA.',
      footClientOne: 'cliente',
      footClientMany: 'clientes',
    },
    extratos: {
      title: 'Extratos',
      subtitle: 'Quem entregou o extrato bancário e quem ainda o deve, por período.',
      colClient: 'Cliente',
      colConsultor: 'Consultor',
      colAccess: 'Acesso',
      colCadence: 'Periodicidade',
      colExtratos: 'Extratos',
      colAction: 'Ação',
      automatic: 'Automático',
      directAccess: 'Acesso direto',
      toReceive: 'A receber',
      cadenceUnset: '— por definir',
      cadenceUnsetHint: 'Defina a cadência do extrato na ficha do cliente (Admin).',
      received: 'Recebido',
      partial: 'Parcial',
      missing: 'Em falta',
      pending: 'Em curso',
      allReceived: 'Todos os extratos recebidos',
      noneDue: 'Sem períodos a vencer no intervalo',
      notify: 'Notificar',
      notifyAll: 'Notificar todos',
      notifiedToday: 'Notificado hoje',
      notifyTitle: 'Notificar cliente sobre extratos em falta',
      notifyAllTitle: 'Notificar clientes com extratos em falta',
      notifyConfirmLead: 'Vai preparar uma notificação (rascunho) para {client}.',
      notifyAllConfirmLead:
        'Vai preparar {n} notificações (rascunho), uma por cada cliente com extratos em falta e ainda não notificado hoje.',
      notifyConfirmPeriods: 'Períodos em falta: {periods}.',
      notifyConfirmDraft: 'Fica na fila como rascunho (pendente) — nada é enviado automaticamente.',
      notifyConfirmCta: 'Preparar rascunho',
      notifyAllConfirmCta: 'Preparar rascunhos',
      // Email body — bulleted list of the missing periods between intro and outro.
      // {periods} is replaced with one "• <período>" per line.
      notifyMsgGreeting: 'Exmos. Senhores,',
      notifyMsgIntro: 'Verificámos que estão em falta os seguintes extratos bancários:',
      notifyMsgOutro: 'Agradecemos o envio dos documentos em falta com a maior brevidade possível.',
      notifyMsgSignature: 'Com os melhores cumprimentos,\nJA Consultores',
      notifyMessage: '{greeting}\n\n{intro}\n\n{periods}\n\n{outro}\n\n{signature}',
      notifyQueued: 'Rascunho de notificação criado.',
      notifyAllQueued: '{n} rascunhos de notificação criados.',
      notifyAllNone: 'Sem clientes a notificar.',
      notifyError: 'Não foi possível criar a notificação.',
      statusLabel: 'Estado',
      statusMissing: 'Em falta',
      statusComplete: 'Completo',
      statusAutomatic: 'Automático',
      cadenceFilterLabel: 'Periodicidade',
      // Totals footer
      footClientOne: 'cliente',
      footClientMany: 'clientes',
      footReceived: 'Recebidos',
      footMissing: 'Em falta',
      footToNotify: 'Por notificar',
      empty: 'Sem clientes para mostrar.',
      loadError: 'Erro a carregar extratos.',
      noStatementDoc: 'Sem documento de extrato para este período.',
      openDetail: 'Ver detalhe por mês e banco',
      viewDoc: 'Ver',
      statement: 'Extrato',
      otherStatements: 'Outros',
      notApplicable: 'Extratos obtidos pelo banco / automaticamente — nada a pedir ao cliente',
      dlgBank: 'Banco',
      dlgReceived: 'Recebido',
      dlgMonth: 'Mês',
    },
    sendLog: {
      // Recusas do Reenviar. A RPC recusa de propósito (excepção, não silêncio) e estas frases
      // são o que torna essa recusa visível — antes o erro era deitado fora pelo chamador.
      resendBlockedNoClient:
        'Este documento não tem empresa atribuída, por isso não há para onde o enviar. Atribua a empresa e volte a tentar.',
      resendBlockedHold:
        'Este documento está marcado para nunca ser encaminhado (sem NIF de adquirente, já lançado no ERP manualmente, ou é um lote que já foi separado — nesse caso reenviá-lo duplicaria as facturas das peças).',
      resendBlockedArchived:
        'Este documento está arquivado ou dispensado, por isso o envio automático não o considera. Restaure-o primeiro, se quiser voltar a enviá-lo.',
      resendForbidden: 'Não tem permissões para reenviar documentos.',
      title: 'Registo de envios',
      origin: 'Origem',
      emailSubject: 'Assunto do e-mail',
      showEmailBody: 'Ver conteúdo do e-mail',
      hideEmailBody: 'Ocultar conteúdo do e-mail',
      channels: {
        email: 'E-mail',
        whatsapp: 'WhatsApp',
        upload: 'Carregamento',
        dashboard_upload: 'Carregamento',
        drop_folder: 'Pasta de destino',
      } as Record<string, string>,
      loading: 'A carregar…',
      notSent: 'Ainda não encaminhado.',
      routed: 'Enviado',
      evaluated: 'Avaliado (simulação)',
      skipped: 'Não enviado',
      orphan: 'Envio preso — recuperação',
      nameRepaired: 'Nome do fornecedor reparado (canónico do registo AT)',
      zeroTrust: {
        wouldHold: 'Validação (sombra): reteria em Pendentes',
        annotated: 'Validação (sombra): seguiria, com {n} achado(s)',
        clean: 'Validação (sombra): tudo conforme',
        validators: {
          nif_check_digit: 'Dígito de controlo do NIF',
          supplier_in_registry: 'Fornecedor no registo',
          supplier_name_registry: 'Nome vs registo',
          acquirer_is_client: 'Adquirente = cliente',
          vat_arithmetic: 'Aritmética do IVA',
          date_plausible: 'Plausibilidade da data',
          type_vs_qr: 'Tipo vs QR fiscal',
          value_grounded: 'NIF consta no texto do documento',
          client_nif_in_doc: 'NIF do cliente no documento',
        } as Record<string, string>,
        // O evento não traz valores — traz a ESPÉCIE de falha. Os números vêem-se no documento.
        notes: {
          bad_check_digit: 'dígito de controlo inválido',
          unknown_supplier: 'fornecedor desconhecido do registo',
          name_mismatch: 'nome diferente do registado',
          future_date: 'data posterior à recepção',
          too_old: 'data demasiado antiga',
          unparseable: 'data ilegível',
          no_source: 'sem fonte independente para verificar',
          sum_mismatch: 'a soma não fecha',
          not_in_text: 'não consta no texto do documento',
          rival_client_nif: 'o documento imprime o NIF de outro cliente',
        } as Record<string, string>,
      },
      nameRepairedSource: 'Fonte',
      delivered: 'Entregue',
      bounced: 'Devolvido',
      recipient: 'Destinatário',
      subject: 'Assunto',
      messageId: 'ID mensagem',
      contisResult: 'Resultado Contis',
      gatedBy: 'Bloqueado por',
      reason: 'Razão',
      awaitingToconline: 'Aguarda resposta do TOConline',
      toconlineRejected: 'TOConline recusou',
      toconlineMessage: 'Mensagem do TOConline',
      toconlineReasons: {
        duplicate: 'já consta no arquivo (duplicado)',
        invalid_type: 'tipo de ficheiro inválido',
        other: 'erro',
      } as Record<string, string>,
      file: 'Ficheiro',
      duplicateFiled: 'Já em arquivo (TOConline) — arquivado',
      resendRequested: 'Reenvio solicitado',
      resend: 'Reenviar para o sistema',
      resending: 'A reenviar…',
      resendConfirm:
        'Isto vai voltar a enviar o documento para o TOConline/Contis no próximo ciclo. Continuar?',
      // Mostrado ANTES do aviso genérico quando o documento já tem um envio confirmado: o botão
      // continua a funcionar (reenviar é o que ele faz), mas o operador tem de saber que o
      // resultado é uma segunda cópia nos livros do cliente, não uma correcção de um envio falhado.
      resendConfirmDelivered:
        'Atenção: este documento JÁ foi entregue ao sistema do cliente. Reenviá-lo cria uma SEGUNDA cópia da factura na contabilidade dele — só o faça se for mesmo isso que pretende.',
    },
    stub: {
      documentosTitle: 'Documentos',
      documentosSubtitle: 'Vista global de todos os documentos da plataforma.',
      placeholder: 'Em desenvolvimento.',
      placeholderHint: 'Esta secção será ativada numa fase posterior. Volte mais tarde.',
    },
    visaoGeral: {
      greeting: {
        manha: 'Bom dia, {name}.',
        tarde: 'Boa tarde, {name}.',
        noite: 'Boa noite, {name}.',
      },
      cycleMeta: '{month} {year} · ciclo a fechar',
      deadlineCountdown: '{days} dias para o prazo do dia {day}',
      deadlineReached: 'Prazo do dia atingido',
      kpi: {
        clientes: 'Clientes',
        clientesUnit: 'clientes',
        clientesOpen: '{open} em aberto · {progress} em curso',
        recebidos: 'Recebidos',
        atencao: 'Atenção',
        atencaoUnit: 'clientes',
        atencaoBreakdown: '{missing} em falta · {illegible} ilegíveis',
      },
      states: {
        title: 'Estado dos documentos',
        subtitle: 'de {total} recebidos',
        classified: 'Identificados',
        submitted: 'Encaminhados',
        pending: 'Pendentes',
        errors: 'Erros',
      },
      sources: {
        email: 'Email',
        upload: 'Plataforma',
        whatsapp: 'WhatsApp',
        drop: 'Pasta',
      },
      recolha: {
        title: 'Previsão Recolha',
        rangeWeek: 'S',
        rangeMonth: 'M',
        rangeYear: 'A',
        legendReceived: 'Recebidos',
        legendForecast: 'Previsão histórica',
        comingSoon: 'Em breve',
      },
      issueMonth: {
        title: 'Documentos por data do documento',
        subtitle:
          'Por mês de emissão (campo "Data do documento") · recebidos vs. encaminhados no período.',
      },
      attention: {
        title: 'A precisar de atenção',
        subtitleWithCount: '{count} clientes com pendentes antes do prazo',
        filtersBtn: 'Filtros',
        notifyAllBtn: 'Notificar todos',
        client: 'Cliente',
        priority: 'Prio.',
        state: 'Estado',
        forecast: 'Previsão',
        action: 'Acção',
        notify: 'Notificar',
        review: 'Rever',
        confirm: 'Confirmar',
        resend: 'Reenviar',
        comingSoonTooltip: 'Disponível na Fase 1e',
        empty: 'Sem clientes em atenção.',
        showing: 'A mostrar {from}–{to} de {total}',
        stateBankMissing: 'Extrato em falta',
        stateIllegible: 'Doc. ilegível ({n})',
        stateAwaiting: 'Aguarda resposta · {days} dias',
        stateForecast: 'Previsão indica {pct}% em falta',
      },
    },
    adminTabs: {
      analytics: 'Analytics',
      contabilidade: 'Contabilidade',
      clients: 'Clientes',
      onboarding: 'Onboarding',
      consultants: 'Consultores',
      risco: 'Risco',
      learning: 'Aprendizagem',
      notificacoes: 'Notificações',
      clientUsers: 'Utilizadores',
      firms: 'Escritórios',
      suppliers: 'Fornecedores',
      roberto: 'Roberto',
      novidades: 'Novidades',
    },
    // /admin/contabilidade — correlação fornecedor ↔ classificação contabilística.
    contabilidadeAdmin: {
      title: 'Contabilidade por fornecedor',
      subtitle:
        'Como os documentos de cada fornecedor foram classificados na contabilidade — por empresa e em toda a carteira. É a base estatística do futuro motor de classificação automática.',
      clientFilter: 'Empresa',
      searchPlaceholder: 'Pesquisar por NIF ou nome…',
      supplierCount: '{n} fornecedores',
      colSupplier: 'Fornecedor',
      colDocs: 'Docs',
      colDominant: 'Classificação dominante',
      colDistribution: 'Outras classificações',
      colClients: 'Empresas',
      colLastUsed: 'Último uso',
      unknownSupplier: '(fornecedor sem nome)',
      docNamePrefix: 'Nos documentos:',
      chartClasses: 'Classificações dominantes',
      chartSuppliers: 'Fornecedores com mais documentos',
      chartCompanies: 'Atribuições por empresa',
      chartOther: 'Outras',
      platformNamePrefix: 'Na plataforma:',
      allClassifications: 'Todas as classificações',
      allClients: 'Todas as empresas',
      loadFailed: 'Não foi possível carregar os dados.',
      empty: 'Sem classificações contabilísticas registadas — importe histórico ou classifique documentos primeiro.',
    },
    // /admin/roberto — o registo das interações do assistente. Existe porque uma
    // resposta de IA pode estar errada e é aqui que se esclarece o que o Roberto
    // disse de facto a um cliente.
    roberto: {
      title: 'Roberto — interações',
      subtitle:
        'O que os clientes perguntam ao assistente, o que ele respondeu, os documentos que usou e quanto custou.',
      loadFailed: 'Não foi possível carregar as interações.',
      tiles: {
        interactions: 'Interações',
        interactionsSub: '{companies} empresas · {askers} utilizadores',
        cost: 'Custo no período',
        costSub: '{avg} por pergunta',
        emptyRate: 'Sem resposta',
        emptyRateSub: '{n} perguntas sem documentos encontrados',
        failRate: 'Erros',
        failRateSub: '{n} falhas · {ms} ms em média',
      },
      droppedTitle: 'Citações descartadas',
      droppedBody:
        'O modelo tentou citar {n} documentos que a pesquisa não devolveu. Foram bloqueados antes de chegarem ao cliente — mas se este número subir, vale a pena rever as respostas.',
      charts: {
        volume: 'Perguntas por dia',
        cost: 'Custo por dia',
        none: 'Sem interações no período.',
        emptyShort: 'sem resposta',
        failedShort: 'erros',
        max: 'máx.',
      },
      byClient: {
        title: 'Por empresa',
        company: 'Empresa',
        interactions: 'Perguntas',
        empty: 'Sem resposta',
        cost: 'Custo',
        last: 'Última',
        none: 'Nenhuma empresa usou o assistente neste período.',
        filterHint: 'Clique para filtrar o registo por esta empresa',
        filtered: 'a filtrar',
      },
      log: {
        title: 'Registo de interações',
        searchPlaceholder: 'Procurar na pergunta ou na resposta…',
        outcome: 'Resultado',
        cost: 'Custo',
        costOver: 'acima de {v}',
        clear: 'Limpar filtros',
        when: 'Quando',
        company: 'Empresa',
        question: 'Pergunta',
        result: 'Resultado',
        docs: 'Docs',
        staff: 'equipa',
        loading: 'A carregar…',
        none: 'Ainda não há interações.',
        noneFiltered: 'Nenhuma interação corresponde aos filtros.',
        pageOf: 'Página {page} de {pages} · {total} interações',
      },
      outcomes: {
        answered: 'Respondida',
        empty: 'Sem resposta',
        failed: 'Erro',
      },
      detail: {
        title: 'Interação',
        close: 'Fechar',
        question: 'Pergunta',
        answer: 'Resposta',
        noAnswer: 'Não houve resposta — o pedido falhou ({code}).',
        documents: 'Documentos citados ({n})',
        loading: 'A carregar documentos…',
        noDocuments: 'Nenhum documento foi citado.',
        open: 'Abrir',
        gone: 'Este documento já não existe (arquivado, separado ou removido).',
        dropped:
          '{n} citações foram descartadas nesta resposta por não corresponderem a documentos devolvidos pela pesquisa.',
      },
    },
    learning: {
      title: 'Aprendizagem da IA',
      subtitle:
        'O que a equipa ensinou (regras) e o que a IA aprendeu sozinha (sugestões) a partir das vossas correções.',
      rulesTitle: 'Regras',
      rulesHint:
        'Criadas quando alguém corrige um documento e marca "Criar regra". Aplicadas a TODOS os documentos futuros do fornecedor.',
      hintsTitle: 'Sugestões aprendidas',
      hintsHint:
        'Geradas automaticamente do histórico de correções; influenciam (sem forçar) a extração da IA em todos os documentos de despesa do fornecedor.',
      colSupplier: 'Fornecedor (NIF)',
      colField: 'Campo',
      colValue: 'Valor',
      colSource: 'Origem',
      colSupport: 'Correções',
      colLearned: 'Aprendido',
      colFired: 'Aplicada',
      colReCorrected: 'Corrigida depois',
      colWrongRate: '% erro',
      firedHint:
        'Vezes que a regra/sugestão foi aplicada · corrigidas de novo depois = provável erro.',
      sourceManual: 'Manual',
      sourceDerived: 'Derivada',
      remove: 'Remover',
      removeConfirm: 'Remover esta regra? Deixa de ser aplicada a documentos futuros.',
      removeRevertConfirmFmt:
        '{n} documento(s) alterado(s) por esta regra serão revertidos ao valor anterior (apenas os que ainda têm o valor da regra).',
      suppress: 'Suprimir',
      suppressConfirm:
        'Suprimir esta sugestão? Deixa de influenciar a IA e não volta a ser gerada nas próximas atualizações.',
      emptyRules: 'Ainda não há regras. Corrija um documento e marque "Criar regra".',
      emptyHints: 'Ainda não há sugestões aprendidas.',
      loadError: 'Não foi possível carregar os dados.',
      impactTitle: 'Impacto real',
      impactHint:
        'Taxa de correção (por 100 docs) em fornecedores com aprendizagem vs os restantes. Menor = melhor.',
      impactLearned: 'Com aprendizagem',
      impactBaseline: 'Sem aprendizagem',
      impactPer100: '{n} / 100 docs',
      impactDocsFmt: '{n} docs',
      impactNoData: 'Ainda sem dados suficientes.',
    },
    analytics: {
      title: 'Analítica da plataforma',
      subtitle: 'Métricas operacionais e custos da plataforma na janela selecionada.',
      windowLabel: 'Janela',
      windowOptions: {
        d7: 'Últimos 7 dias',
        d30: 'Últimos 30 dias',
        d90: 'Últimos 90 dias',
      },
      sections: {
        ai: 'IA',
        storage: 'Armazenamento',
      },
      storage: {
        totalLabel: 'Armazenamento',
        nominalCostLabel: 'Custo nominal R2',
        nominalCostSub: '€0,014 por GB·mês (preço Cloudflare)',
        avgPerDocLabel: 'Tamanho médio / doc',
        avgPerDocSub: 'apenas docs com tamanho registado',
        coveredFmt: '{covered}/{total} docs com tamanho registado',
        docsInWindowFmt: '{count} docs na janela',
        growthTitle: 'Crescimento de armazenamento',
        modeMonthly: 'Mensal',
        modeYoY: 'Ano vs ano',
        empty: 'Sem dados de armazenamento na janela.',
        addedLegend: 'Adicionado no mês',
        cumulativeLegend: 'Total acumulado',
        thisYearLegend: 'Este ano',
        lastYearLegend: 'Ano anterior',
      },
      tiles: {
        docsReceived: 'Docs recebidos',
        aiCost: 'Custo IA',
        // O «Custo IA» acima é o total. Estes dois separam-no: interacção (Roberto e assistente de
        // regras) vs processamento de documentos. Somam sempre o total — sem isso não se consegue
        // preçar o Roberto separadamente do processamento.
        aiCostInteraction: 'Custo IA — interação',
        aiCostInteractionSub: 'Roberto e assistente',
        aiCostDocuments: 'Custo IA — documentos',
        aiCostDocumentsSub: 'Processamento',
        aiCostDrift:
          'Há {n} prompt(s) por classificar — o custo deles está a contar como documentos. Ver f_analytics_spend_breakdown.',
        aiCalls: 'Chamadas IA',
        aiSuccess: 'Taxa de sucesso',
      },
      ai: {
        title: 'Utilização de IA',
        latencyP50: 'Latência p50',
        latencyP95: 'Latência p95',
        modelMixTitle: 'Por modelo',
        modelHeader: 'Modelo',
        callsHeader: 'Chamadas',
        costHeader: 'Custo',
        avgLatencyHeader: 'Latência média',
        successHeader: 'Sucesso',
        empty: 'Sem chamadas de IA na janela selecionada.',
      },
      throughput: {
        title: 'Volume de documentos',
        dailyTitle: 'Diário',
        receivedLegend: 'Recebidos',
        identifiedLegend: 'Identificados',
        routedLegend: 'Encaminhados',
        failedLegend: 'Não identificados',
        missingLegend: 'Não recebidos (vs. esperado)',
        channelTitle: 'Por canal',
        empty: 'Sem documentos na janela.',
        modeState: 'Por estado',
        modeChannel: 'Por canal',
      },
      routing: {
        title: 'Encaminhamento',
        outcomeHeader: 'Resultado',
        pipelineHeader: 'Pipeline',
        countHeader: 'Total',
        outcomes: {
          sent: 'Enviado',
          evaluated: 'Avaliado (dry-run)',
          'skipped:phc_pipeline_pending': 'PHC pendente',
          'skipped:missing_client': 'Cliente desconhecido',
          'skipped:missing_client_nif': 'NIF em falta',
          'skipped:unknown_pipeline': 'Pipeline inválido',
          'skipped:cas_conflict': 'Conflito de transição',
        },
        unknownOutcome: 'Outro',
        empty: 'Sem eventos de encaminhamento na janela.',
      },
      quality: {
        title: 'Qualidade da identificação',
        identifiedLabel: 'Identificados',
        editedLabel: 'Editados manualmente',
        rateLabel: 'Taxa de edição',
        hint: 'Quanto menor melhor — proxy para precisão da identificação automática.',
      },
      byClient: {
        title: 'Por cliente',
        subtitle: 'Custo e volume atribuídos a cada cliente. Clique para abrir o detalhe.',
        nifHeader: 'NIF',
        nameHeader: 'Cliente',
        docsHeader: 'Docs',
        callsHeader: 'Chamadas IA',
        costHeader: 'Custo IA',
        skippedHeader: 'Não encaminhados',
        oversizedHeader: 'Grandes (>25MB)',
        empty: 'Sem atividade por cliente na janela.',
      },
      loading: 'A carregar análise…',
      loadFailed: 'Não foi possível carregar a análise.',
    },
    detail: {
      back: '← Voltar',
      breadcrumb: 'Por cliente',
      notFound: 'Cliente não encontrado.',
      loading: 'A carregar…',
      issueMonthTitle: 'Documentos por data do documento',
      issueMonthSubtitle: 'Por mês de emissão · últimos 12 meses · recebidos vs. encaminhados.',
      kpiReceived: 'recebidos',
      kpiExpected: 'esperados',
      headerMeta: {
        nifPrefix: 'NIF',
        channelPrefix: 'Canal principal:',
        carteiraPrefix: 'Carteira:',
      },
      statementReceived: 'Extrato bancário recebido',
      statementMissing: 'Sem extrato bancário',
      statementPending: 'Extrato em falta',
      progressTitle: 'Progresso global — {month}',
      progressPct: '{pct}% concluído',
      progressSubmitted: 'Submetidos ({count})',
      progressClassified: 'Classificados ({count})',
      progressPending: 'Por processar ({count})',
      filters: {
        allCount: 'Todos ({n})',
        errorsCount: 'Erros ({n})',
        pendingCount: 'Pendentes ({n})',
        submittedCount: 'Submetidos ({n})',
        classifiedCount: 'Classificados ({n})',
        typeAll: 'Tipo ▾',
        searchDoc: 'Pesquisar documento...',
        supplierLabel: 'Fornecedor',
        supplierAll: 'Fornecedor: todos',
        supplierSearch: 'Procurar fornecedor…',
        // Reception date = documents.created_at (the "Recebido" column), not the
        // emission date — a March invoice sent in July filters as July.
        receivedDateLabel: 'Data de recebimento',
        receivedDateAny: 'Qualquer data',
      },
      table: {
        doc: 'Documento',
        type: 'Tipo',
        baseValue: 'Valor base',
        iva: 'IVA',
        total: 'Total',
        supplier: 'Fornecedor',
        // The analytical column carried the literal string 'Etiquetas', the same header as the
        // markers column beside it — a leftover from the 2026-09-03 swap. Naming per that decision:
        // client_tags = Analítica, client_markers = Etiquetas.
        analitica: 'Analítica',
        contabilidade: 'Contabilidade',
        etiquetas: 'Etiquetas',
        status: 'Status',
        channel: 'Canal',
        received: 'Recebido',
        issueDate: 'Data emissão',
        action: 'Ação',
        empty: 'Nenhum documento para este filtro.',
        view: 'Ver',
        edit: 'Editar',
        retry: 'Reprocessar',
        archive: 'Arquivar',
        archiveTitle: 'Arquivar — excluir dos totais',
        unarchive: 'Restaurar',
        unarchiveTitle: 'Restaurar — voltar a contar',
        restoreConfirm: 'Restaurar este documento para a pipeline ativa?',
        moreActions: 'Mais ações',
        bulkArchive: 'Arquivar selecionados',
        bulkUnarchive: 'Restaurar selecionados',
        delete: 'Eliminar',
        deleteTitle: 'Eliminar — remover permanentemente',
        deleteConfirm: 'Eliminar o documento',
        deleteWarning: 'Esta ação é permanente e não pode ser revertida.',
      },
      edit: {
        title: 'Editar documento',
        // The client asked us to remove this document (portal "Pedir eliminação"). Only staff can
        // act; arquivar/dispensar answers it implicitly, "Recusar" answers it explicitly.
        deletionRequest: {
          title: 'O cliente pediu a eliminação deste documento',
          hint: 'Arquivar ou dispensar o documento fecha o pedido automaticamente.',
          decline: 'Recusar pedido',
          badge: 'Eliminação pedida',
        },
        // The client told us this document is mis-catalogued (portal "Perguntar ao
        // contabilista"). Corrigir o documento não fecha a pergunta — fechá-la é explícito,
        // porque só quem responde sabe se a questão ficou de facto resolvida.
        question: {
          title: 'O cliente colocou uma questão sobre este documento',
          hint: 'Corrija o documento e depois marque a questão como respondida.',
          resolve: 'Marcar como respondida',
          applySuggestions: 'Aplicar sugestões',
          // Field labels for the client's proposed corrections. Same words the edit form uses, so
          // the accountant reads the suggestion and the field it lands in as the same thing.
          fields: {
            supplierName: 'Fornecedor',
            supplierNif: 'NIF do fornecedor',
            issueDate: 'Data de emissão',
            totalAmount: 'Total',
            vatAmount: 'IVA',
            documentType: 'Tipo de documento',
          },
          badge: 'Questão aberta',
        },
        clientLabel: 'Cliente',
        clientSearch: 'Pesquisar por nome ou NIF…',
        clientCurrent: 'Cliente atual',
        sentBy: 'Enviado por',
        suggestions: 'Empresas sugeridas (email)',
        whatsappShared: 'Número WhatsApp partilhado — atribuir empresa:',
        stateLabel: 'Estado',
        supplierLabel: 'Fornecedor',
        supplierNifLabel: 'NIF do fornecedor (opcional)',
        // Entity type derived from the NIF prefix (nifEntityType), shown read-only as
        // context to the reviewer. nifTypeUnknown = a prefix that isn't a valid PT NIF
        // start (foreign VAT or a misread).
        nifTypes: {
          individual: 'Particular',
          individual_nonresident: 'Particular (não residente)',
          company: 'Empresa',
          company_nonresident: 'Empresa (não residente)',
          public: 'Entidade pública',
          inheritance: 'Herança indivisa',
          investment_fund: 'Fundo de investimento',
          official: 'Atribuição oficiosa',
          official_nonresident: 'Atribuição oficiosa (não residente)',
          special_regime: 'Regime especial',
          sole_trader: 'Empresário em nome individual',
          condominium: 'Condomínio',
          nonresident_no_establishment: 'Não residente',
          civil_partnership: 'Sociedade civil',
        },
        nifTypeUnknown: 'Sem tipo PT válido — estrangeiro ou erro de leitura',
        supplierNamePrior: 'Visto antes como',
        documentTypeLabel: 'Tipo de documento',
        totalLabel: 'Total',
        vatLabel: 'IVA',
        currencyLabel: 'Moeda',
        issueDateLabel: 'Data de emissão',
        clientBanksLabel: 'Bancos do cliente',
        clientBanksEmpty: 'Nenhum banco configurado',
        bankAddPlaceholder: 'Adicionar banco (ex.: Wise)',
        bankAddBtn: 'Adicionar',
        bankAdded: 'Banco adicionado à ficha do cliente.',
        bankAddError: 'Não foi possível adicionar o banco.',
        // Maker-checker (#96): the structured reason + free-text detail, approval mode only.
        aiSectionLabel: 'Aprendizagem da IA',
        reasonLabel: 'Tipo de correção',
        reasonNone: '— selecionar —',
        reasonOptions: {
          transport_pass: 'Passe de transporte',
          vat_net_as_gross: 'IVA (total era sem IVA)',
          date_swap: 'Data trocada (ano/dia)',
          wrong_client_direction: 'Cliente / sentido errado',
          supplier_misread: 'Fornecedor mal lido',
          other: 'Outro',
        },
        tagLabel: 'Porquê esta correção? (opcional)',
        tagPlaceholder: 'Ex.: Passe Lisboa Viva; fatura isenta de IVA…',
        analiticaTags: 'Analítica',
        contabTags: 'Contabilidade',
        tagHint: 'A regra que justifica a correção — fica registada e ajuda a IA a aprender.',
        makeRule: 'Criar regra: documentos deste fornecedor são sempre deste tipo.',
        save: 'Guardar alterações',
        saving: 'A guardar…',
        saved: 'Alterações guardadas.',
        saveError: 'Não foi possível guardar as alterações.',
        confirmReassign: 'Está a atribuir este documento a outro cliente. Continuar?',
        confirmRoute:
          'Isto liberta o documento para ser enviado ao sistema contabilístico (TOConline/PHC) no próximo ciclo de encaminhamento. Continuar?',
        // #91 client-notification suggestion (operator wiring; queues a PENDING row, never sends).
        notify: {
          resendTitle: 'Sugerir reenvio ao cliente',
          advisoryTitle: 'Avisar o cliente (risco fiscal)',
          reason: {
            missing_nif_or_total: 'Falta NIF e/ou total',
            illegible: 'Documento ilegível',
            not_fiscally_deductible: 'Pode não ser dedutível',
          },
          channelLabel: 'Canal',
          channelEmail: 'Email',
          channelWhatsapp: 'WhatsApp',
          queueButton: 'Pôr em fila',
          queuing: 'A processar…',
          queued: '✓ Em fila (pendente — envio manual pela firma)',
          draftNotice:
            'Rascunho — a redação final é da firma (compliance). Nada é enviado automaticamente.',
        },
      },
      status: {
        classified: 'Classificado',
        submitted: 'Submetido',
        pending: 'Pendente',
        error: 'Erro',
        illegible: 'Ilegível',
        received: 'Recebido',
      },
      docType: {
        invoice: 'Fatura',
        credit_note: 'Nota de Crédito',
        debit_note: 'Nota de Débito',
        receipt: 'Recibo',
        bank_statement: 'Extrato bancário',
        transfer_receipt: 'Comprovativo de Transferência',
        travel_expense: 'Despesa de viagem',
        state_payment: 'Pagamento ao Estado',
        recibo_vencimento: 'Recibo de Vencimento',
        mapa_quilometros: 'Mapa de Quilómetros',
        ajudas_custo: 'Ajudas de Custo',
        purchase_order: 'Nota de encomenda',
        transport_pass: 'Passe de transporte',
        meal_voucher_topup: 'Carregamento cartão refeição',
        financial_statement: 'Balanço / Demonstração',
        email_thread: 'Email',
        signature_image: 'Imagem de assinatura',
        back_of_invoice: 'Verso de fatura',
        download_link: 'Ligação para download',
        other: 'Outro',
      },
    },
  },
} as const

export type Translations = typeof pt
